// src/Components/EventWizard/MinimalDashboard.jsx
import React, { useState, useMemo, useEffect, useRef } from 'react';
import StudioCamPresetPanel from './StudioCamPresetPanel';
import MinimalMpdCamToggler from './MinimalMpdCamToggler';
import LivekitEventPanel from './LivekitEventPanel';
import { POLL_MS, confirmDanger } from './lib/ui';
import { getDashMediaPlayerLiveSettings } from '@into-reality/player';
import { authFetch } from '../../utils/authFetch';

const API_BASE =
  (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, '')) ||
  'http://localhost:4000';

const COLORS = { ok: '#4caf50', err: '#f44336', idle: '#9e9e9e', warn: '#ff9800', neutral: '#3f51b5' };

// Drift polling is one request per camera through the backend proxy to each EC2,
// so it runs slower than POLL_MS (2s) — drift moves in seconds, not milliseconds,
// and a 6-camera event at 2s would mean 3 req/s of pure monitoring traffic.
const DRIFT_POLL_MS = 5000;

// Above this spread the cameras are visibly out of sync with each other.
const DRIFT_SPREAD_WARN_S = 1.0;

// The local `styles` object below only carries `badge`, so buttons style inline.
const driftBtn = (bg, enabled = true) => ({
  backgroundColor: bg,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  fontSize: 14,
  fontWeight: 600,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 1 : 0.5,
});

function detectRoleFromInst(inst) {
  const raw = String(inst?.role || inst?.kind || inst?.type || '').toLowerCase();
  if (raw === 'audio' || raw === 'video') return raw;
  const name = String(inst?.camName || '').toLowerCase();
  if (name.includes('mic') || name.includes('audio') || name.includes('voice')) return 'audio';
  return 'video';
}

const BADGE_MIN_WIDTH = 88;   // consistent width across pills
const BADGE_HEIGHT = 26;      // consistent height
const styles = {
  badge: (bg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 0px',
    minWidth: BADGE_MIN_WIDTH,
    height: BADGE_HEIGHT,
    borderRadius: BADGE_HEIGHT / 2,
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 18,
    backgroundColor: bg,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  }),
};
const Badge = ({ ok, on = COLORS.ok, off = COLORS.err, labelOk = 'OK', labelOff = 'OFF' }) => (
  <span style={styles.badge(ok ? on : off)}>{ok ? labelOk : labelOff}</span>
);
const Pill = ({ color = COLORS.warn, children }) => <span style={styles.badge(color)}>{children}</span>;

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

// --- helpers to ensure commands are saved & uploaded ---
const detectRole = (inst) => {
  const raw = String(inst?.role || inst?.kind || inst?.type || '').toLowerCase();
  if (raw === 'audio' || raw === 'video') return raw;
  const name = String(inst?.camName || '').toLowerCase();
  if (name.includes('mic') || name.includes('audio') || name.includes('voice')) return 'audio';
  return 'video';
};
const fetchEventCommandMap = async (api, evt) => {
  const resp = await api.getEvent(evt);
  const arr = resp?.data?.commands || [];
  const map = {};
  for (const c of arr) {
    if (c.instanceId && typeof c.command === 'string' && c.command.trim()) {
      map[c.instanceId] = c.command.trim();
    }
  }
  return map;
};
const fetchRoleDefaults = async (api) => {
  const pr = await api.listPresets();
  const presets = pr?.data?.presets || [];
  const defVideo = presets.find((p) => p.isDefault && (p.role || p.type) === 'video') || null;
  const defAudio = presets.find((p) => p.isDefault && (p.role || p.type) === 'audio') || null;
  return { video: defVideo?.command || null, audio: defAudio?.command || null };
};
const ensureCommandsUploaded = async (api, setMessage, evt, cams) => {
  const [cmdMap, roleDefaults] = await Promise.all([
    fetchEventCommandMap(api, evt),
    fetchRoleDefaults(api),
  ]);
  for (const inst of cams) {
    const role = detectRole(inst);
    const selected = cmdMap[inst.instanceId];
    const desired = selected || (role === 'audio' ? roleDefaults.audio : roleDefaults.video);
    if (!desired) {
      setMessage?.(`No saved preset or role default in DB for ${inst.camName} (${role}); skipping this cam.`);
      continue;
    }
    try {
      await api.saveEventCmd(evt, { instanceId: inst.instanceId, command: desired, role });
      await api.updateNodeScript(inst.instanceId, desired);
    } catch (e) {
      setMessage?.(`Warning: could not push FFmpeg to ${inst.camName}: ${e.message}`);
    }
  }
};

// Pushes each cam's declared ingest profile down to its instance. Instances are
// terminated and relaunched per event, and the controller's ingest-config.json dies
// with the AMI — so the DB re-seeds it here, right before /ffmpeg/prepare, which is
// where syncSlate and syncNormalizer read it. A cam with nothing declared is left
// alone: the instance keeps whatever it already had.
const ensureIngestProfilesUploaded = async (api, setMessage, evt, cams) => {
  let profiles = {};
  let normalizerQuality = null;
  try {
    const r = await api.getIngestProfiles(evt);
    profiles = r.data?.ingestProfiles || {};
    normalizerQuality = r.data?.normalizerQuality || null;
  } catch (e) {
    setMessage?.(`Warning: could not read ingest profiles: ${e.message}`);
    return;
  }
  // Event-level normalizer quality override rides in the same per-instance
  // push; the controller applies it when composing normalizer.sh at start.
  const nq = normalizerQuality || {};
  const boolKey = v => (typeof v === 'boolean' ? (v ? 1 : 0) : '');
  const normKeys = {
    normalizerPreset:      nq.preset || '',
    normalizerTemporalAq:  boolKey(nq.temporalAq),
    normalizerSpatialAq:   boolKey(nq.spatialAq),
    normalizerAqStrength:  nq.aqStrength ?? '',
    normalizerBitrateMbps: nq.bitrateMbps ?? '',
  };
  // Push + VERIFY per instance: the controller's response echoes back the
  // declared config it actually persisted, so a divergence (network hiccup,
  // older controller ignoring the normalizer keys, validation reject) gets a
  // name instead of silently starting the event with a stale config.
  const failed = [];
  for (const inst of cams) {
    const p = profiles[inst.camName] || {};
    if (!profiles[inst.camName] && !normalizerQuality) continue;
    try {
      const r = await api.setIngestConfig(inst.instanceId, {
        width:  p.width  ?? '',
        height: p.height ?? '',
        codec:  p.codec  ?? '',
        kbps:   p.kbps   ?? '',
        ...normKeys,
      });
      if (normalizerQuality) {
        const applied = r.data?.declared?.normalizer || {};
        const wantPreset = nq.preset || null;
        const gotPreset  = applied.preset || null;
        if (wantPreset !== gotPreset) {
          failed.push(`${inst.camName} (normalizer preset: wanted ${wantPreset || 'default'}, instance has ${gotPreset || 'default'})`);
        }
      }
    } catch (e) {
      failed.push(`${inst.camName} (push failed: ${e.response?.data?.errors?.join('; ') || e.message})`);
    }
  }
  if (failed.length) {
    setMessage?.(`⚠ Ingest config NOT verified on: ${failed.join(' · ')} — these cams may run with a stale normalizer config.`);
  }
};

const SPEED_OK_THRESHOLD = 0.95;
function renderSpeedPill(val) {
  // Accept numbers or strings like "0.98" / "0.98x"
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (!Number.isFinite(num)) {
    return <span style={styles.badge(COLORS.idle)} title="No speed">-</span>;
  }
  const color = num > SPEED_OK_THRESHOLD ? COLORS.ok : COLORS.warn;
  const label = `${num.toFixed(2)}x`;
  return <span style={styles.badge(color)} title={`Speed: ${label}`}>{label}</span>;
}

export default function MinimalDashboard({
  grouped,
  rtmpOk,
  ingestOk,
  ingestKbps,
  ffmpegStat,
  speedMap,
  setSpeedMap = () => {},
  sourceOverrideMap = {},
  onRefresh,
  setMessage,
  onGenerateAndUploadMpd,
  api,
  pendingLaunchesByEvent = {},
  presets = [],
  manifestText = {},
  manifestMeta = {},
  showManifest = {},
  setShowManifest = () => {},
  fetchManifestForEvent,
  onUploadTemplateMpd,
  setManifestText = () => {},
  // Advanced mode props
  advancedMode = false,
  cmdFromDb = {},
  onPresetsReload,
  fetchFull,
  backendUrl = '',
  manifestText8K = {},
  manifestTextFull = {},
  setManifestText8K = () => {},
  setManifestTextFull = () => {},
  saveAndPushMpd,
  dashSettingsContent = {},
  setDashSettingsContent,
  saveDashSettings,
}) {
  const [selectedCamInstanceId, setSelectedCamInstanceId] = useState(null);
  const [showPresetPanel, setShowPresetPanel] = useState(false);
  const [presetPanelLoading, setPresetPanelLoading] = useState(false);

  // Advanced mode: expanded row state
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedTab, setExpandedTab] = useState('preset');
  const [editCmd, setEditCmd] = useState('');
  const [logText, setLogText] = useState('');
  const [advPresetId, setAdvPresetId] = useState('');
  const [advPresetName, setAdvPresetName] = useState('');
  const [advPresetDesc, setAdvPresetDesc] = useState('');

  // Per-camera selection for Start Event (instanceId → boolean)
  const [startSelection, setStartSelection] = useState({});

  // Manifest editor panel state
  const [mpdOpen, setMpdOpen] = useState(false);
  const [mpdTab, setMpdTab] = useState('main'); // 'main' | '8k' | 'full'
  const [mpdEdit, setMpdEdit] = useState({});   // { [evt]: text }
  const [mpdEdit8K, setMpdEdit8K] = useState({});
  const [mpdEditFull, setMpdEditFull] = useState({});
  const [mpdSaving, setMpdSaving] = useState(false);

  // Dash Settings panel state
  const [dashSettingsOpen, setDashSettingsOpen] = useState(false);
  const [dashSettingsEdit, setDashSettingsEdit] = useState({});
  const [dashSettingsSaving, setDashSettingsSaving] = useState(false);

  // Player Settings panel state

  // Server Config panel state
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfgTab, setCfgTab] = useState('srs'); // 'srs' | 'controller'
  const [cfgText, setCfgText] = useState('');
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfgResults, setCfgResults] = useState(null); // [{ instanceId, camName, ... }]

  // Quality-based failover (starved detection) — OFF by default on the
  // controller: only a real ingest loss switches to slate. This advanced
  // toggle enables the delivery-ratio analysis for all cams of the event.
  // { [evt]: true | false | null(unknown) }
  const [starvedSwitchByEvt, setStarvedSwitchByEvt] = useState({});
  const loadFailoverState = async (evt, camsOnly) => {
    const first = camsOnly[0];
    if (!first) return;
    try {
      const r = await api.getFailover(first.instanceId);
      setStarvedSwitchByEvt(prev => ({ ...prev, [evt]: !!r.data?.starvedSwitch }));
    } catch {
      setStarvedSwitchByEvt(prev => ({ ...prev, [evt]: null }));
    }
  };
  // Normalizer quality override (per event): the controller default (p4 +
  // temporal AQ) eats ~52% of NVENC alone — measured 2026-09-18. Applies at
  // the NEXT event start (pushed with the ingest profiles; syncNormalizer
  // regenerates + restarts only if the script actually changed).
  const [normQualByEvt, setNormQualByEvt] = useState({}); // { [evt]: { preset, temporalAq } | null }
  const loadNormalizerQuality = async (evt) => {
    try {
      const r = await api.getIngestProfiles(evt);
      setNormQualByEvt(prev => ({ ...prev, [evt]: r.data?.normalizerQuality || null }));
    } catch { /* panel shows defaults */ }
  };
  const saveNormalizerQuality = async (evt, quality) => {
    setCfgLoading(true);
    try {
      await api.saveNormalizerQuality(evt, quality || {});
      setNormQualByEvt(prev => ({ ...prev, [evt]: quality }));
      setMessage?.(`Normalizer quality saved for "${evt}" — applies at the next event start.`);
    } catch (e) {
      setMessage?.(`Save failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setCfgLoading(false);
    }
  };

  const toggleFailover = async (evt, camsOnly) => {
    const next = !(starvedSwitchByEvt[evt] === true);
    setCfgLoading(true);
    try {
      await Promise.all(camsOnly.map(inst => api.setFailover(inst.instanceId, next).catch(() => {})));
      setStarvedSwitchByEvt(prev => ({ ...prev, [evt]: next }));
      setMessage?.(`Quality failover ${next ? 'ENABLED' : 'disabled'} on ${camsOnly.length} cam(s). Not persisted: resets to OFF on instance restart.`);
    } finally {
      setCfgLoading(false);
    }
  };

  // Slate logo upload state
  const [slateLogoBase64, setSlateLogoBase64] = useState(null);
  const [slateLogoName, setSlateLogoName] = useState('');
  const [slateUploading, setSlateUploading] = useState(false);
  const [slateResult, setSlateResult] = useState(null);

  // Declared ingest profiles: the rig's real parameters per camera, so slate +
  // normalizer can be sized before the camera publishes (that's what allows starting
  // an event on the logo alone). Persisted in Mongo keyed by camName — instances are
  // relaunched per event and their local ingest-config.json dies with the AMI.
  // Latency target: what the manifest tells the player, and how far it could come
  // down given this session's camera offsets.
  const [latencyState, setLatencyState] = useState(null);   // { targetMs, info }
  const [latencyDraft, setLatencyDraft] = useState('');
  const [latencySaving, setLatencySaving] = useState(false);

  const [ingestDrafts, setIngestDrafts] = useState({});     // camName → editable {width,height,codec,kbps}
  const [ingestSuggested, setIngestSuggested] = useState({}); // camName → prefill derived from last run's MPD
  const [ingestSaving, setIngestSaving] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  const [ingestState, setIngestState] = useState({});       // instanceId → live {declared, measured, effective, mismatch}

  // Editable cam labels (persisted in DB)
  const [camLabels, setCamLabels] = useState({});
  const labelTimers = useRef({});

  // Load labels from DB when events change
  useEffect(() => {
    if (!grouped || !api) return;
    Object.keys(grouped).forEach(async (evt) => {
      try {
        const r = await api.getCamLabels(evt);
        if (r.data?.success) {
          setCamLabels(prev => ({ ...prev, ...Object.fromEntries(Object.entries(r.data.camLabels || {})) }));
        }
      } catch {}
    });
  }, [grouped, api]);

  const updateCamLabel = (evt, instanceId, label) => {
    setCamLabels(prev => ({ ...prev, [instanceId]: label }));
    // Debounce save to DB (500ms)
    clearTimeout(labelTimers.current[instanceId]);
    labelTimers.current[instanceId] = setTimeout(() => {
      api.saveCamLabel(evt, instanceId, label).catch(() => {});
    }, 500);
  };

  // Editable content offsets per camera (persisted in DB, keyed by camName)
  const [contentOffsets, setContentOffsets] = useState({});
  const offsetTimers = useRef({});

  // Load content offsets from DB when events change
  useEffect(() => {
    if (!grouped || !api) return;
    Object.keys(grouped).forEach(async (evt) => {
      try {
        const r = await api.getContentOffsets(evt);
        if (r.data?.success) {
          // Replace this event's entries instead of merging: a plain merge kept
          // stale values alive after the session reset cleared them in the DB
          // (ghost offsets from the previous session shown in the panel,
          // 2026-10-01). Drop this event's cams first, then apply what the DB
          // actually has.
          setContentOffsets(prev => {
            const next = { ...prev };
            for (const inst of grouped[evt] || []) {
              const k = (inst.camName || '').toLowerCase();
              if (k) delete next[k];
            }
            for (const [k, v] of Object.entries(r.data.contentOffsets || {})) next[k] = v;
            return next;
          });
        }
      } catch {}
    });
  }, [grouped, api]);

  const updateContentOffset = (evt, camName, value) => {
    const key = (camName || '').toLowerCase();
    setContentOffsets(prev => ({ ...prev, [key]: value }));
    clearTimeout(offsetTimers.current[key]);
    offsetTimers.current[key] = setTimeout(() => {
      api.saveContentOffset(evt, camName, value).catch(() => {});
    }, 500);
  };

  // 15s grace window per camera (avoid "Failed" within the window)
  const [graceUntilByInstanceId, setGraceUntilByInstanceId] = useState({});
  const isInGrace = (id) => (graceUntilByInstanceId[id] || 0) > Date.now();

  // Abort mechanism for autoStartEvent
  const startAbortRef = useRef(false);
  const [startingEvent, setStartingEvent] = useState(false);
  const [forcingSource, setForcingSource] = useState(false);

  // MPD fragment enabled states: { "evt/camName/role" -> boolean }
  const [fragStates, setFragStates] = useState({});

  const refreshFragStates = async (evt) => {
    try {
      const r = await api.getEvent(evt);
      const frags = r.data?.mpdFragments || [];
      setFragStates(prev => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (k.startsWith(`${evt}/`)) delete next[k];
        }
        for (const f of frags) {
          const key = `${evt}/${(f.camName || '').toLowerCase()}/${f.role}`;
          next[key] = f.enabled !== false;
        }
        return next;
      });
    } catch {}
  };

  const getFragEnabled = (evt, camName, role) => {
    const key = `${evt}/${(camName || '').toLowerCase()}/${role}`;
    return fragStates[key];
  };

  // === NEW: First segment times (per event) ===
  const [firstSegByEvent, setFirstSegByEvent] = useState({}); // { [evt]: { loadedAt, rows: [] } }
  const [openFirstSegEvt, setOpenFirstSegEvt] = useState(null);
  const [firstSegLoadingEvt, setFirstSegLoadingEvt] = useState(null);

  // === Elastic IPs (advanced): fixed camera ips that follow the fresh
  // instances of each event, associated from Studio instead of the AWS console.
  const [eips, setEips] = useState(null);          // null = not loaded
  const [eipSel, setEipSel] = useState({});        // instanceId → allocationId
  const [eipBusy, setEipBusy] = useState(false);
  const loadEips = async () => {
    try {
      const r = await api.listElasticIps();
      setEips(r.data?.addresses || []);
    } catch (e) {
      setMessage?.(`Could not list Elastic IPs: ${e.response?.data?.message || e.message}`);
      setEips([]);
    }
  };
  useEffect(() => {
    if (advancedMode && eips === null) loadEips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedMode]);
  const associateEip = async (inst) => {
    const allocationId = eipSel[inst.instanceId];
    if (!allocationId) return;
    const eip = (eips || []).find(a => a.allocationId === allocationId);
    if (eip?.instanceId && eip.instanceId !== inst.instanceId) {
      if (!confirmDanger(
        `${eip.name ? `"${eip.name}" (${eip.publicIp})` : eip.publicIp} is currently attached to "${eip.instanceName}".\n\n` +
        `Re-associating will TAKE it from that instance (its public ip changes and ` +
        `anything pointing at it goes dark). Continue?`
      )) return;
    }
    setEipBusy(true);
    try {
      await api.associateEip(inst.instanceId, allocationId);
      setMessage?.(`${eip?.publicIp || 'Elastic IP'} associated to ${inst.camName}. Refreshing…`);
      await loadEips();
      onRefresh?.(); // the instance's public ip just changed — reload the list
    } catch (e) {
      setMessage?.(`Associate failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setEipBusy(false);
    }
  };

  // === LiveKit (WHIP) path — parallel to DASH, per event ===
  const [streamingModeByEvent, setStreamingModeByEvent] = useState({}); // { [evt]: 'dash'|'livekit' }
  const [livekitIngressByEvent, setLivekitIngressByEvent] = useState({}); // { [evt]: { [camName]: {...} } }
  const [livekitParticipantsByEvent, setLivekitParticipantsByEvent] = useState({}); // { [evt]: { [identity]: participant } }

  // `silent` distinguishes the 5s auto-poll (a transient failure just retries
  // next tick — surfacing it would spam the message bar) from a user-initiated
  // refresh, where a failure must be visible or it looks identical to "no
  // ingress created yet".
  const refreshLivekitIngress = async (evt, { silent = false } = {}) => {
    try {
      const r = await api.getLivekitIngress(evt);
      setLivekitIngressByEvent(prev => ({ ...prev, [evt]: r.data?.ingress || {} }));
    } catch (e) {
      if (!silent) setMessage?.(`LiveKit ingress status check failed for "${evt}": ${e.response?.data?.message || e.message}`);
    }
  };

  // The real confirmation a camera is live in the room — not just that the
  // ingress connected. Same check as `GET /api/rooms/:name/participants`.
  const refreshLivekitParticipants = async (evt, { silent = false } = {}) => {
    try {
      const r = await api.getLivekitParticipants(evt);
      const byIdentity = Object.fromEntries((r.data?.participants || []).map(p => [p.identity, p]));
      setLivekitParticipantsByEvent(prev => ({ ...prev, [evt]: byIdentity }));
    } catch (e) {
      if (!silent) setMessage?.(`LiveKit participants check failed for "${evt}": ${e.response?.data?.message || e.message}`);
    }
  };

  // Auto-poll for events in LiveKit mode: ingress state (publishing/error),
  // in-room participants and viewer count refresh on their own, matching how
  // the DASH badges already behave. Serialized, visibility-aware; reads the
  // mode map through a ref so re-renders don't tear the timer down.
  const LIVEKIT_POLL_MS = 5000;
  const streamingModeRef = useRef(streamingModeByEvent);
  useEffect(() => { streamingModeRef.current = streamingModeByEvent; }, [streamingModeByEvent]);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') {
        const evts = Object.entries(streamingModeRef.current || {})
          .filter(([, m]) => m === 'livekit')
          .map(([e]) => e);
        for (const evt of evts) {
          if (cancelled) return;
          await refreshLivekitIngress(evt, { silent: true });
          await refreshLivekitParticipants(evt, { silent: true });
        }
      }
      if (!cancelled) setTimeout(tick, LIVEKIT_POLL_MS);
    };
    const t = setTimeout(tick, LIVEKIT_POLL_MS);
    return () => { cancelled = true; clearTimeout(t); };
    // refresh helpers only close over `api` (memoised) and setters — stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setEventMode = async (evt, mode) => {
    setStreamingModeByEvent(prev => ({ ...prev, [evt]: mode })); // optimistic
    try {
      await api.setStreamingMode(evt, mode);
      if (mode === 'livekit') {
        refreshLivekitIngress(evt);
        refreshLivekitParticipants(evt);
      }
    } catch (e) {
      setMessage?.(`Could not switch mode: ${e.response?.data?.message || e.message}`);
    }
  };

  // === Drift panel (per event) ===
  // The per-camera monitor popup already shows /drift, but one camera at a time,
  // which is useless for sync: what matters is how far the cameras are from EACH
  // OTHER, and that only shows up when you look at them together.
  const [driftByEvent, setDriftByEvent] = useState({});   // { [evt]: { loadedAt, rows: [] } }
  const [openDriftEvt, setOpenDriftEvt] = useState(null);
  const [driftLoadingEvt, setDriftLoadingEvt] = useState(null);

  // === Track order (IR-196): operator-defined cam/audio order in the manifest ===
  // The first video/audio track is the viewer default, so the order is an
  // editorial decision, not an infra one — hence its own panel instead of the
  // instance table (which lists EC2 boxes, not manifest tracks).
  const [trackOrderByEvent, setTrackOrderByEvent] = useState({}); // { [evt]: { videos: [], audios: [], dirty } }
  const [openTrackOrderEvt, setOpenTrackOrderEvt] = useState(null);
  const [trackOrderBusyEvt, setTrackOrderBusyEvt] = useState(null);

  const normCam = (s) => String(s || '').toLowerCase().trim();

  const fetchTrackOrder = async (evt) => {
    try {
      setTrackOrderBusyEvt(evt);
      const { data } = await api.getEvent(evt);
      const frags = (data?.mpdFragments || []).filter(f => f.enabled !== false);
      const dedupe = (role) => {
        const seen = new Set(); const out = [];
        for (const f of frags) {
          if (f.role !== role) continue;
          const key = normCam(f.camName);
          if (!key || seen.has(key)) continue;
          seen.add(key); out.push({ camName: f.camName, instanceId: f.instanceId });
        }
        return out;
      };
      // Mirrors the backend's sortByTrackOrder so the panel shows what the
      // manifest actually emits: saved order first, name sort as fallback.
      const sortWith = (tracks, orderList) => {
        const order = new Map((orderList || []).map((n, i) => [normCam(n), i]));
        return tracks.slice().sort((a, b) => {
          const ia = order.has(normCam(a.camName)) ? order.get(normCam(a.camName)) : Infinity;
          const ib = order.has(normCam(b.camName)) ? order.get(normCam(b.camName)) : Infinity;
          return ia !== ib ? ia - ib : String(a.camName).localeCompare(String(b.camName), undefined, { numeric: true, sensitivity: 'base' });
        });
      };
      // Human labels, resolved the same way the viewer sees them: Event
      // cam/audio names are positional over the CURRENT manifest order, and
      // camLabels (by instanceId) wins for audio like in the manifest build.
      let camLabels = {};
      try { const r = await api.getCamLabels(evt); camLabels = r?.data?.camLabels || {}; } catch {}
      let evInfo = null;
      try {
        const r = await authFetch(`${API_BASE}/event/get`);
        const j = await r.json();
        evInfo = (j?.events || []).find(e => e.title === evt) || null;
      } catch {}
      const vids = sortWith(dedupe('video'), data?.camOrder);
      const auds = sortWith(dedupe('audio'), data?.audioOrder);
      setTrackOrderByEvent(prev => ({
        ...prev,
        [evt]: {
          videos: vids.map((t, i) => ({ camName: t.camName, label: camLabels[t.instanceId] || evInfo?.cam_names?.[i] || '' })),
          audios: auds.map((t, i) => ({ camName: t.camName, label: camLabels[t.instanceId] || evInfo?.audio_names?.[i] || '' })),
          dirty: false,
        },
      }));
      setOpenTrackOrderEvt(evt);
    } catch (e) {
      setMessage?.(`Track order load failed: ${e.message}`);
    } finally {
      setTrackOrderBusyEvt(null);
    }
  };

  const moveTrack = (evt, kind, index, delta) => {
    setTrackOrderByEvent(prev => {
      const cur = prev[evt]; if (!cur) return prev;
      const list = cur[kind].slice();
      const j = index + delta;
      if (j < 0 || j >= list.length) return prev;
      [list[index], list[j]] = [list[j], list[index]];
      return { ...prev, [evt]: { ...cur, [kind]: list, dirty: true } };
    });
  };

  const applyTrackOrder = async (evt) => {
    const cur = trackOrderByEvent[evt]; if (!cur) return;
    if (!window.confirm(
      `Apply the new camera/audio order to "${evt}" and rebuild the manifest?\n\n` +
      `The first camera/audio becomes the viewer default. Event labels and thumbnails are re-ordered to match.`
    )) return;
    try {
      setTrackOrderBusyEvt(evt);
      setMessage?.('Saving track order…');
      await api.setTrackOrder(evt, {
        camOrder: cur.videos.map(v => v.camName),
        audioOrder: cur.audios.map(a => a.camName),
      });
      const masterInst = Object.values(grouped).flat()
        .find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
      await api.rebuildMpd(evt, [], masterInst?.instanceId);
      if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
      setTrackOrderByEvent(prev => ({ ...prev, [evt]: { ...prev[evt], dirty: false } }));
      setMessage?.(`Track order applied for "${evt}" and manifest rebuilt.`);
    } catch (e) {
      setMessage?.(`Track order failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setTrackOrderBusyEvt(null);
    }
  };
  // `grouped` is rebuilt by a plain reduce in the parent, so it is a new object on
  // every render. Reading it through a ref keeps it out of the poll effect's deps —
  // otherwise each render tore the effect down and restarted the timer, and since
  // renders arrive faster than the interval the tick never fired at all.
  const groupedRef = useRef(grouped);
  useEffect(() => { groupedRef.current = grouped; }, [grouped]);

  // Track in-flight manifest fetches per event to avoid overlapping calls
  const inflightRef = useRef({}); // { [evt]: boolean }

// put this near the top
const initiallyFetched = useRef({}); // { [evt]: true }

// Auto-select RTMP-OK cameras for Start Event. Not just on first appearance:
// an instance usually shows up BEFORE its SRS is reachable, so a select-once
// froze it unselected until a page reload re-ran the "first" pass. Instead,
// promote to selected whenever RTMP comes up — unless the operator touched
// that checkbox manually, in which case their choice always wins.
const manualSelectionRef = useRef({}); // { [instanceId]: true } once hand-toggled
useEffect(() => {
  if (!grouped) return;
  setStartSelection(prev => {
    let changed = false;
    const next = { ...prev };
    for (const list of Object.values(grouped)) {
      for (const inst of list) {
        if ((inst.role || '').toLowerCase() === 'master') continue;
        const id = inst.instanceId;
        if (!(id in next)) {
          next[id] = !!rtmpOk[id];
          changed = true;
        } else if (!manualSelectionRef.current[id] && rtmpOk[id] && !next[id]) {
          next[id] = true;
          changed = true;
        }
      }
    }
    return changed ? next : prev;
  });
}, [grouped, rtmpOk]);

// keep the set clean if events disappear
useEffect(() => {
  const live = new Set(Object.keys(grouped || {}));
  Object.keys(initiallyFetched.current).forEach(k => {
    if (!live.has(k)) delete initiallyFetched.current[k];
  });
}, [grouped]);

// fetch once per event when it first appears
useEffect(() => {
  if (!grouped) return;
  for (const [evt, list] of Object.entries(grouped)) {
    if (initiallyFetched.current[evt]) continue;
    const masterInst = (list || []).find(i => (i.role || '').toLowerCase() === 'master');
    fetchManifestForEvent?.(evt, masterInst);
    initiallyFetched.current[evt] = true;
  }
  // intentionally omit fetchManifestForEvent to avoid re-firing when its identity changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [grouped]);

  // Load fragment enabled states when events appear
  useEffect(() => {
    if (!grouped || !api) return;
    Object.keys(grouped).forEach(async (evt) => {
      try {
        const r = await api.getEvent(evt);
        const frags = r.data?.mpdFragments || [];
        setFragStates(prev => {
          const next = { ...prev };
          for (const f of frags) {
            const key = `${evt}/${(f.camName || '').toLowerCase()}/${f.role}`;
            next[key] = f.enabled !== false;
          }
          return next;
        });
        const mode = r.data?.streamingMode || 'dash';
        setStreamingModeByEvent(prev => ({ ...prev, [evt]: mode }));
        if (mode === 'livekit') {
          setLivekitIngressByEvent(prev => ({ ...prev, [evt]: r.data?.livekitIngress || {} }));
          refreshLivekitParticipants(evt);
        }
      } catch {}
    });
  }, [grouped, api]);

  // ─────────────────────────────────────────────────────────────
  // NEW: Poll manifests via the master proxy at POLL_MS (5s)
  // * Only when the event is RUNNING or its manifest is explicitly opened
  // * Serialized, visibility-aware, and de-duplicated with in-flight guard
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!grouped || !fetchManifestForEvent) return;
    let cancelled = false;
    let timer;

    const scheduleNext = () => {
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        if (document.visibilityState !== 'visible') return scheduleNext();

        for (const [evt, list] of Object.entries(grouped)) {
          const masterInst = (list || []).find(i => (i.role || '').toLowerCase() === 'master');
          if (!masterInst) continue;

          // Only poll if event is running OR user opened this event's manifest panel
          const camsInEvt = (list || []).filter(i => (i.role || '').toLowerCase() !== 'master');
          const running = camsInEvt.some(c => !!ffmpegStat[c.instanceId]);
          const userRequested = !!showManifest?.[evt];
          if (!running && !userRequested) continue;

          if (inflightRef.current[evt]) continue; // skip if already fetching this event
          inflightRef.current[evt] = true;
          try {
            await fetchManifestForEvent(evt, masterInst);
          } finally {
            inflightRef.current[evt] = false;
          }
        }
      } catch {
        // ignore transient errors
      }
      scheduleNext();
    };

    scheduleNext();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [grouped, ffmpegStat, showManifest, fetchManifestForEvent]);
  // ─────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────
  // Refresh the drift panel while it is open. Read-only: it observes, it never
  // corrects. Only the open event is polled, and only while the tab is visible,
  // so closing the panel costs nothing.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!openDriftEvt) return;
    let cancelled = false;
    let timer;

    const tick = async () => {
      if (cancelled) return;
      const list = groupedRef.current?.[openDriftEvt];
      if (list && document.visibilityState === 'visible') {
        try { await fetchDrift(openDriftEvt, list, { silent: true }); } catch {}
      }
      if (!cancelled) timer = setTimeout(tick, DRIFT_POLL_MS);
    };

    timer = setTimeout(tick, DRIFT_POLL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDriftEvt]);
  // ─────────────────────────────────────────────────────────────

  async function fetchFirstSegTimes(evt) {
    try {
      setFirstSegLoadingEvt(evt);
      const r = await authFetch(`${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/first-segment-times`);
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.message || `HTTP ${r.status}`);
      setFirstSegByEvent(prev => ({
        ...prev,
        [evt]: {
          loadedAt: new Date().toISOString(),
          rows: j.results || []
        }
      }));
      setOpenFirstSegEvt(evt);
    } catch (e) {
      setMessage?.(`First segment fetch error: ${e.message}`);
    } finally {
      setFirstSegLoadingEvt(null);
    }
  }

  // The AMI has shipped /drift under both snake_case and camelCase over time, so
  // read either rather than silently rendering blanks on the variant we guessed
  // wrong.
  const pickNum = (...vals) => {
    for (const v of vals) {
      const n = Number(v);
      if (v != null && Number.isFinite(n)) return n;
    }
    return null;
  };
  const pickVal = (...vals) => vals.find(v => v != null) ?? null;

  async function fetchDrift(evt, list, { silent = false } = {}) {
    const cams = (list || []).filter(i => (i.role || '').toLowerCase() !== 'master');
    if (!cams.length || !api) return;
    if (!silent) setDriftLoadingEvt(evt);
    try {
      const rows = await Promise.all(cams.map(async (inst) => {
        const base = { instanceId: inst.instanceId, camName: inst.camName };
        try {
          const d = (await api.getDrift(inst.instanceId))?.data || {};
          return {
            ...base,
            ok: true,
            lagSeconds:   pickNum(d.lag_seconds, d.lagSeconds),
            driftSeconds: pickNum(d.accumulated_drift_seconds, d.accumulatedDriftSeconds),
            activeSource: pickVal(d.active_source, d.activeSource),
            switchCount:  pickVal(d.switch_count, d.switchCount),
            lastSwitch:   pickVal(d.last_switch, d.lastSwitch),
            lastSegment:  pickVal(d.last_segment_number, d.lastSegmentNumber),
            demoshAlive:  pickVal(d.demosh_alive, d.demoshAlive),
          };
        } catch (e) {
          return { ...base, ok: false, error: e.message };
        }
      }));
      // How far the AST has already been walked back. /drift never reflects it
      // (it reads each camera's own cam.mpd), so without this the panel would keep
      // offering the same shift on an unchanged reading, over and over.
      let astShiftSeconds = 0;
      let astOriginal = null;
      try {
        const ev = (await api.getEvent(evt))?.data || {};
        astShiftSeconds = Number(ev.astShiftSeconds) || 0;
        astOriginal = ev.availabilityStartTimeOriginal || null;
      } catch {}

      setDriftByEvent(prev => ({
        ...prev,
        [evt]: { loadedAt: new Date().toISOString(), rows, astShiftSeconds, astOriginal },
      }));
      setOpenDriftEvt(evt);
    } finally {
      if (!silent) setDriftLoadingEvt(null);
    }
  }

  /**
   * Turn per-camera drift into per-camera misalignment.
   *
   * /drift reports each encoder's own lead over wallclock, which on its own says
   * nothing about sync: if every camera is 2s ahead they are perfectly aligned.
   * What maps to a content offset is the DIFFERENCE against a reference camera.
   *
   * MORE drift means the encoder ran ahead of wallclock, so that camera's live edge
   * carries OLDER content and the viewer sees it as extra latency: the highest-drift
   * camera is the one furthest BEHIND, not the lowest. Verified on a 2-camera live —
   * drift 3.00s / 1.00s measured as 17s / 15s of player latency.
   *
   * So the reference is the camera with the HIGHEST drift, and every other camera is
   * delayed to meet it. That way no camera is ever asked to advance past its own live
   * edge; the cost is that the faster cameras give up some latency, which is the
   * documented trade-off of aligning to the slowest.
   */
  function driftAnalysis(rows = []) {
    const usable = rows.filter(r => r.ok && r.driftSeconds != null);
    if (usable.length < 2) return { usable, ref: null, spread: null, byCam: {} };

    const ref = usable.reduce((a, b) => (b.driftSeconds > a.driftSeconds ? b : a));
    const min = usable.reduce((a, b) => (b.driftSeconds < a.driftSeconds ? b : a));
    const byCam = {};
    for (const r of usable) {
      byCam[(r.camName || '').toLowerCase()] = Math.round((ref.driftSeconds - r.driftSeconds) * 10) / 10;
    }
    return { usable, ref, spread: Math.round((ref.driftSeconds - min.driftSeconds) * 10) / 10, byCam };
  }

  // visible status computation (uses grace + ffmpegStat)
  function getEncoderStatus(instId) {
    if (isInGrace(instId)) {
      return { label: 'Running', color: COLORS.ok };
    }
    return ffmpegStat[instId]
      ? { label: 'Running', color: COLORS.ok }
      : { label: 'Stopped', color: COLORS.err };
  }

  const terminateAllForEvent = async (evt, list) => {
    const ok = window.confirm(
      `Are you sure you want to TERMINATE ALL instances for the event "${evt}"?\n\n` +
        `This will terminate the MASTER (if any) and ALL camera instances.`
    );
    if (!ok) return;
    try {
      setMessage?.(`Terminating all instances for "${evt}"…`);
      await Promise.all(list.map((inst) => api.terminate(inst.instanceId).catch((e) => e)));
      setMessage?.(`All instances for "${evt}" terminated.`);
      onRefresh?.();
    } catch (e) {
      setMessage?.(`Terminate-all error: ${e.message}`);
    }
  };

  const autoStartEvent = async (evt, list) => {
    startAbortRef.current = false;
    setStartingEvent(true);
    const aborted = () => startAbortRef.current;

    try {
      const masterInst = list.find((i) => (i.role || '').toLowerCase() === 'master');
      if (!masterInst) {
        setMessage?.(`No master for event "${evt}"`);
        setStartingEvent(false);
        return;
      }

      const camsOnly = list
        .filter((i) => (i.role || '').toLowerCase() !== 'master')
        .sort((a, b) =>
          a.camName.localeCompare(b.camName, undefined, { numeric: true, sensitivity: 'base' })
        );

      // Check if any cam is already encoding
      if (camsOnly.some(c => !!ffmpegStat[c.instanceId])) {
        setMessage?.(`Event "${evt}" is already running.`);
        setStartingEvent(false);
        return;
      }

      const eligibleCams = camsOnly.filter(
        (inst) => !!rtmpOk[inst.instanceId] && startSelection[inst.instanceId]
      );
      if (!eligibleCams.length) {
        setMessage?.(`No selected cameras with RTMP OK in "${evt}"`);
        setStartingEvent(false);
        return;
      }

      // Reset stale event session data (AST, fragments, MPD) from previous runs
      try {
        setMessage?.('Resetting event session data…');
        await api.resetEventSession(evt);
        // Mirror the reset in local UI state — the DB just cleared this
        // event's contentOffsets, and without this the panel keeps showing
        // the previous session's values until a full page reload.
        setContentOffsets(prev => {
          const next = { ...prev };
          for (const inst of camsOnly) {
            const k = (inst.camName || '').toLowerCase();
            if (k) delete next[k];
          }
          return next;
        });
      } catch (e) {
        setMessage?.(`Warning: session reset failed: ${e.message}`);
      }

      setMessage?.('Applying preselected or DB-default presets to instances…');
      await ensureCommandsUploaded(api, setMessage, evt, eligibleCams);

      // Must land before /ffmpeg/prepare: that's where syncSlate + syncNormalizer
      // read it to size the chain for a camera that may not be publishing yet.
      setMessage?.('Pushing declared ingest profiles to instances…');
      await ensureIngestProfilesUploaded(api, setMessage, evt, eligibleCams);

      // ───────── Template MPD before starting cameras ─────────
      try {
        setMessage?.('Uploading minimal template MPD to master path…');
        if (typeof onUploadTemplateMpd === 'function') {
          await onUploadTemplateMpd(evt, list, masterInst);
        } else {
          await authFetch(
            `${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/manifest/template`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ masterInstanceId: masterInst.instanceId }),
            }
          );
        }
        setMessage?.('Template MPD published.');
      } catch (e) {
        setMessage?.(`Warning: Could not upload template MPD (continuing): ${e.message}`);
      }
      // ──────────────────────────────────────────────────────────

      await sleep(1000);

      // ═══════════ PHASE 1: Prepare all cameras in parallel ═══════════
      if (aborted()) { setMessage?.('Start aborted.'); setStartingEvent(false); return; }
      // Absorbs variable-time I/O (archive, mount, slate) before spawning FFmpeg.
      // One archiveTs for the whole start: every camera archives the previous
      // session into the same archived_<ts>/ snapshot, so the archived master's
      // "../<cam>/" references keep resolving.
      const archiveTs = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').slice(0, 19);
      setMessage?.(`Preparing ${eligibleCams.length} camera(s) (archive, mount, slate)…`);
      const prepareResults = await Promise.allSettled(
        eligibleCams.map(inst =>
          api.prepareFfmpeg(inst.instanceId, evt, inst.camName, archiveTs)
            .then(r => ({ cam: inst.camName, ok: r?.data?.prepared !== false, data: r?.data }))
            .catch(e => ({ cam: inst.camName, ok: false, error: e.message }))
        )
      );
      const prepareFailed = prepareResults
        .map(r => r.value || r.reason)
        .filter(r => !r.ok);
      if (prepareFailed.length) {
        setMessage?.(`Warning: Prepare failed on ${prepareFailed.map(f => f.cam).join(', ')}`);
      }

      await sleep(500);

      // ═══════════ PHASE 2: Go — spawn FFmpeg on all cameras simultaneously ═══════════
      if (aborted()) { setMessage?.('Start aborted.'); setStartingEvent(false); return; }
      setMessage?.(`Spawning FFmpeg on ${eligibleCams.length} camera(s) simultaneously…`);
      const goResults = await Promise.allSettled(
        eligibleCams.map(inst =>
          api.goFfmpeg(inst.instanceId, { eventName: evt, camName: inst.camName })
            .then(r => ({ cam: inst.camName, ok: r?.data?.started !== false, data: r?.data }))
            .catch(e => ({ cam: inst.camName, ok: false, error: e.message }))
        )
      );
      const goFailed = goResults
        .map(r => r.value || r.reason)
        .filter(r => !r.ok);
      if (goFailed.length) {
        setMessage?.(`Warning: FFmpeg spawn failed on ${goFailed.map(f => f.cam).join(', ')}`);
      }

      // ═══════════ PHASE 3: Poll all cameras for first segment readiness ═══════════
      // Collect createdAtMs per camera to measure first-segment generation time.
      // This is used in Phase 4 to calculate per-camera delays for alignment.
      setMessage?.('Waiting for all cameras to produce first segments…');
      const POLL_INTERVAL = 2000;
      const POLL_TIMEOUT = 60000;
      const pollStart = Date.now();
      const readyCams = new Set();
      const segmentCreatedAt = {}; // instanceId → createdAtMs

      // Grace period covers entire flow (3 restarts + polls ≈ 3 min max)
      // Prevents UI from showing "Stopped" during warm restart cycles
      const graceUntil = Date.now() + 180000;
      setGraceUntilByInstanceId((prev) => {
        const next = { ...prev };
        for (const c of eligibleCams) next[c.instanceId] = graceUntil;
        return next;
      });

      while (readyCams.size < eligibleCams.length && (Date.now() - pollStart) < POLL_TIMEOUT && !aborted()) {
        const notReady = eligibleCams.filter(c => !readyCams.has(c.instanceId));
        await Promise.allSettled(
          notReady.map(async (inst) => {
            try {
              const r = await api.ffmpegReady(inst.instanceId);
              if (r?.data?.ready) {
                readyCams.add(inst.instanceId);
                if (r.data.createdAtMs) segmentCreatedAt[inst.instanceId] = r.data.createdAtMs;
              }
            } catch {}
          })
        );
        setMessage?.(`Segments ready: ${readyCams.size}/${eligibleCams.length} cameras`);
        if (readyCams.size < eligibleCams.length && !aborted()) await sleep(POLL_INTERVAL);
      }
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }

      if (readyCams.size < eligibleCams.length) {
        const missing = eligibleCams.filter(c => !readyCams.has(c.instanceId)).map(c => c.camName);
        setMessage?.(`Warning: Timed out waiting for segments on: ${missing.join(', ')}`);
      }

      // ═══════════ PHASE 4: Warm restart #1 — measure warm timing ═══════════
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }
      setMessage?.('Warm restart #1: stopping all for timing measurement…');
      await Promise.allSettled(
        eligibleCams.map(inst => api.stopFfmpeg(inst.instanceId).catch(() => {}))
      );
      await sleep(3000);

      setMessage?.(`Warm restart #1: re-launching ${eligibleCams.length} camera(s)…`);
      await Promise.allSettled(
        eligibleCams.map(inst =>
          api.goFfmpeg(inst.instanceId, { eventName: evt, camName: inst.camName }).catch(() => {})
        )
      );

      // Poll ready + collect warm createdAtMs
      const warmCreatedAt = {};
      const readyCamsWarm = new Set();
      const pollStartW = Date.now();
      while (readyCamsWarm.size < eligibleCams.length && (Date.now() - pollStartW) < POLL_TIMEOUT && !aborted()) {
        const notReady = eligibleCams.filter(c => !readyCamsWarm.has(c.instanceId));
        await Promise.allSettled(
          notReady.map(async (inst) => {
            try {
              const r = await api.ffmpegReady(inst.instanceId);
              if (r?.data?.ready) {
                readyCamsWarm.add(inst.instanceId);
                if (r.data.createdAtMs) warmCreatedAt[inst.instanceId] = r.data.createdAtMs;
              }
            } catch {}
          })
        );
        setMessage?.(`Warm timing: ${readyCamsWarm.size}/${eligibleCams.length} cameras`);
        if (readyCamsWarm.size < eligibleCams.length && !aborted()) await sleep(POLL_INTERVAL);
      }
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }

      // Calculate delays from warm measurements
      const warmTimes = Object.values(warmCreatedAt);
      const warmMax = warmTimes.length ? Math.max(...warmTimes) : 0;
      const delayByCam = {};
      for (const inst of eligibleCams) {
        const t = warmCreatedAt[inst.instanceId];
        delayByCam[inst.instanceId] = (t && warmMax) ? Math.round(warmMax - t) : 0;
      }
      const delayInfo = eligibleCams.map(c => `${c.camName}:${delayByCam[c.instanceId]}ms`).join(', ');
      setMessage?.(`Warm delays — ${delayInfo}`);

      // ═══════════ PHASE 5: Warm restart #2 — apply delays for final alignment ═══════════
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }
      setMessage?.('Warm restart #2: stopping all for final aligned launch…');
      await Promise.allSettled(
        eligibleCams.map(inst => api.stopFfmpeg(inst.instanceId).catch(() => {}))
      );
      await sleep(3000);

      setMessage?.(`Final launch: ${eligibleCams.length} camera(s) with warm delays…`);
      const regoResults = await Promise.allSettled(
        eligibleCams.map(inst =>
          api.goFfmpeg(inst.instanceId, {
            eventName: evt,
            camName: inst.camName,
            delayMs: delayByCam[inst.instanceId] || 0,
          })
            .then(r => ({ cam: inst.camName, ok: r?.data?.started !== false, data: r?.data }))
            .catch(e => ({ cam: inst.camName, ok: false, error: e.message }))
        )
      );
      const regoFailed = regoResults
        .map(r => r.value || r.reason)
        .filter(r => !r.ok);
      if (regoFailed.length) {
        setMessage?.(`Warning: Final launch failed on ${regoFailed.map(f => f.cam).join(', ')}`);
      }

      // Poll ready — final
      setMessage?.('Waiting for final synchronized segments…');
      const readyCams2 = new Set();
      const pollStart2 = Date.now();
      while (readyCams2.size < eligibleCams.length && (Date.now() - pollStart2) < POLL_TIMEOUT && !aborted()) {
        const notReady2 = eligibleCams.filter(c => !readyCams2.has(c.instanceId));
        await Promise.allSettled(
          notReady2.map(async (inst) => {
            try {
              const r = await api.ffmpegReady(inst.instanceId);
              if (r?.data?.ready) readyCams2.add(inst.instanceId);
            } catch {}
          })
        );
        setMessage?.(`Final segments ready: ${readyCams2.size}/${eligibleCams.length} cameras`);
        if (readyCams2.size < eligibleCams.length && !aborted()) await sleep(POLL_INTERVAL);
      }
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }

      if (readyCams2.size < eligibleCams.length) {
        const missing2 = eligibleCams.filter(c => !readyCams2.has(c.instanceId)).map(c => c.camName);
        setMessage?.(`Warning: Timed out on final launch for: ${missing2.join(', ')}`);
      }

      // Determine OK cams — those that have segments ready OR are encoding
      const toBool = (v) => {
        if (typeof v === 'boolean') return v;
        if (typeof v === 'number') return v !== 0;
        if (typeof v === 'string') {
          const s = v.trim().toLowerCase();
          if (['true', '1', 'yes', 'on', 'running'].includes(s)) return true;
          if (['false', '0', 'no', 'off', 'stopped', ''].includes(s)) return false;
        }
        return false;
      };

      const okCams = [];
      for (const inst of eligibleCams) {
        if (readyCams2.has(inst.instanceId)) {
          okCams.push(inst);
          continue;
        }
        // Fallback: check status for cameras that didn't produce segments in time
        try {
          const base = await authFetch(`${API_BASE}/event-wizard/proxy/${inst.instanceId}/status`)
            .then((r) => r.json())
            .catch(() => ({}));
          const encOn = toBool(base.ffmpegRunning);
          const ingest = !!ingestOk[inst.instanceId];
          if (encOn || ingest) okCams.push(inst);
        } catch {}
      }

      // Parse cam.mpd and save profiles with bandwidth
      const normalizeCodec = (c) => (typeof c === 'string' && c.trim() === 'hvc1') ? 'hvc1.1.6.L180.90' : c;
      const parseCamMpd = (xmlText) => {
        try {
          const pick = (re, def = '') => {
            const m = xmlText.match(re);
            return m ? m[1] : def;
          };
          const codecs = normalizeCodec(
            pick(/<Representation[^>]*\scodecs="([^"]+)"/i, '') ||
            pick(/<AdaptationSet[^>]*\scodecs="([^"]+)"/i, '')
          );
          const width =
            parseInt(
              pick(/<Representation[^>]*\swidth="(\d+)"/i, '') ||
                pick(/<AdaptationSet[^>]*\swidth="(\d+)"/i, ''),
              10
            ) || 0;
          const height =
            parseInt(
              pick(/<Representation[^>]*\sheight="(\d+)"/i, '') ||
                pick(/<AdaptationSet[^>]*\sheight="(\d+)"/i, ''),
              10
            ) || 0;
          const bandwidth = parseInt(pick(/<Representation[^>]*\sbandwidth="(\d+)"/i, ''), 10) || 0;
          return { codecs, width, height, bandwidth };
        } catch {
          return { codecs: '', width: 0, height: 0, bandwidth: 0 };
        }
      };

      async function fetchCamMpdWithRetry(url, attempts = 12, delayMs = 1500) {
        let lastErr = null;
        for (let i = 1; i <= attempts; i++) {
          try {
            const res = await fetch(`${url}?ts=${Date.now()}`, { cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const xml = await res.text();
            if (xml && xml.includes('<MPD')) return xml;
            throw new Error('Empty/invalid MPD');
          } catch (e) {
            lastErr = e;
            await sleep(delayMs);
          }
        }
        throw lastErr || new Error('cam.mpd not available');
      }

      const camMpdData = [];
      for (const cam of okCams) {
        if (detectRole(cam) !== 'video') continue; // only video cams in event MPD
        const url = `https://d10tner3hocpa9.cloudfront.net/${evt}/${cam.camName}/cam.mpd`;
        setMessage?.(`[cam-mpd] fetching: ${url}`);
        let parsed = null;
        try {
          const xml = await fetchCamMpdWithRetry(url);
          const { codecs, width, height, bandwidth } = parseCamMpd(xml);
          const scale = width && height ? `${width}x${height}` : '';
          setMessage?.(
            `${cam.camName}: codec=${codecs || '(?)'} scale=${scale || '(?)'} bw=${bandwidth ||
              '(?)'} — saving profile…`
          );

          try {
            await authFetch(`${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/cam-profile`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                instanceId: cam.instanceId,
                camName: cam.camName,
                codecs,
                width,
                height,
                scale,
                bandwidth,
              }),
            });
          } catch (e) {
            setMessage?.(`Warning: Could not save profile for ${cam.camName}: ${e.message}`);
          }

          parsed = { codecs, width, height, bandwidth };
        } catch (err) {
          setMessage?.(`${cam.camName} cam.mpd read failed (will include defaults): ${err.message}`);
        }

        camMpdData.push({
          instanceId: cam.instanceId,
          camName: cam.camName,
          publicIp: cam.publicIp,
          role: 'video',
          ...(parsed || {}),
        });
      }

      // Optional backend snapshot (reads cam.mpd server-side as a fallback)
      try {
        await authFetch(
          `${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/snapshot-cam-profiles`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              cams: okCams
                .filter((c) => detectRole(c) === 'video')
                .map((c) => ({ camName: c.camName, instanceId: c.instanceId })),
            }),
          }
        );
      } catch {
        /* non-fatal */
      }

      // Rebuild/upload with ok cams
      try {
        if (typeof onGenerateAndUploadMpd === 'function') {
          setMessage?.(
            `Rebuilding & uploading MPD (${camMpdData.length} video cam(s) included)…`
          );
          await onGenerateAndUploadMpd(evt, list, masterInst, camMpdData, { resetAst: true, forcePtoZero: true });
          setMessage?.(`MPD rebuilt & uploaded.`);
        }
      } catch (e) {
        setMessage?.(`Warning: Error rebuilding/uploading MPD: ${e.message}`);
      }

      // Start publishTime periodic updater on master
      try {
        await api.publishStart(masterInst.instanceId, evt);
        setMessage?.('publishTime updater started on master.');
      } catch (e) {
        setMessage?.(`Warning: publishTime updater failed: ${e.message}`);
      }

      // End grace
      setGraceUntilByInstanceId((prev) => {
        const next = { ...prev };
        for (const c of eligibleCams) delete next[c.instanceId];
        return next;
      });

      setMessage?.(`Auto-start complete for "${evt}".`);
      onRefresh?.();
      await refreshFragStates(evt);
      try { await fetchManifestForEvent?.(evt, masterInst); } catch {}
    } catch (err) {
      setMessage?.(`Auto-start error: ${err.message}`);
    } finally {
      setStartingEvent(false);
    }
  };

  // Selected instance and current command
  const selectedInstance = useMemo(() => {
    if (!selectedCamInstanceId) return null;
    for (const arr of Object.values(grouped)) {
      const inst = arr.find(i => i.instanceId === selectedCamInstanceId);
      if (inst) return inst;
    }
    return null;
  }, [selectedCamInstanceId, grouped]);
  const currentCmd = selectedInstance
    ? (cmdFromDb[selectedInstance.instanceId] || selectedInstance.command || selectedInstance.ffmpegCommand || '')
    : '';

  // Apply preset
  const handleApplyPreset = async (preset) => {
    if (!selectedInstance || !preset) return;
    setPresetPanelLoading(true);
    try {
      await api.saveEventCmd(selectedInstance.eventName, {
        instanceId: selectedInstance.instanceId,
        command: preset.command,
        role: preset.role || detectRole(selectedInstance)
      });
      await api.updateNodeScript(selectedInstance.instanceId, preset.command);
      setMessage?.(`✅ Success: Preset "${preset.name}" applied to ${selectedInstance.camName}`);
      onRefresh?.();
      setShowPresetPanel(false);
      setSelectedCamInstanceId(null);
    } catch (e) {
      setMessage?.(`Error applying preset: ${e.message}`);
    } finally {
      setPresetPanelLoading(false);
    }
  };

  // --- Start/Stop FFmpeg on a single camera ---
  const startSingleCamEncoding = async (evt, inst) => {
    try {
      setMessage?.(`Starting encoding on ${inst.camName}…`);
      // 1) Ensure FFmpeg command is saved & pushed to node
      await ensureCommandsUploaded(api, setMessage, evt, [inst]);
      // 2) Start FFmpeg directly on the node (sends eventName+camName for remote mount)
      //    Mid-event alignment (startNumber/renumber) is not supported: cameras must
      //    start with the event and show the slate/banner until they have signal.
      await api.startFfmpeg(inst.instanceId, evt, inst.camName);
      // 3) Grace period so status shows Running while FFmpeg initializes
      const graceUntil = Date.now() + 15000;
      setGraceUntilByInstanceId(prev => ({ ...prev, [inst.instanceId]: graceUntil }));
      setMessage?.(`Encoding started on ${inst.camName}. Waiting for segments…`);
      onRefresh?.();

      // 4) Wait for segments, then auto-add to MPD
      await sleep(15000);

      // Verify FFmpeg is actually running
      const statusRes = await authFetch(`${API_BASE}/event-wizard/proxy/${inst.instanceId}/status`)
        .then(r => r.json()).catch(() => ({}));
      const toBoolLocal = (v) => {
        if (typeof v === 'boolean') return v;
        if (typeof v === 'string') return ['true','1','yes','running'].includes(v.trim().toLowerCase());
        return false;
      };
      if (!toBoolLocal(statusRes.ffmpegRunning)) {
        setMessage?.(`FFmpeg not running on ${inst.camName} after grace — check logs.`);
        setGraceUntilByInstanceId(prev => { const n = { ...prev }; delete n[inst.instanceId]; return n; });
        return;
      }

      // 5) Snapshot cam profile
      setMessage?.(`Snapshotting profile for ${inst.camName}…`);
      try {
        await authFetch(
          `${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/snapshot-cam-profiles`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cams: [{ camName: inst.camName, instanceId: inst.instanceId }] }),
          }
        );
      } catch {}

      // 6) Add MPD fragment + rebuild
      setMessage?.(`Adding ${inst.camName} to MPD…`);
      await api.addMpdFragment(evt, {
        instanceId: inst.instanceId,
        camName: inst.camName,
        role: (inst.role || '').toLowerCase() === 'audio' ? 'audio' : 'video',
      });

      const masterInst = Object.values(grouped).flat().find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
      await api.rebuildMpd(evt, [], masterInst?.instanceId);
      await refreshFragStates(evt);

      setGraceUntilByInstanceId(prev => { const n = { ...prev }; delete n[inst.instanceId]; return n; });
      await onRefresh?.();
      if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
      setMessage?.(`${inst.camName} encoding + added to MPD.`);
    } catch (err) {
      setMessage?.(`Start encoding error for ${inst.camName}: ${err.message}`);
    }
  };

  const stopSingleCamEncoding = async (evt, inst) => {
    try {
      setMessage?.(`Stopping encoding on ${inst.camName}…`);
      await api.stopFfmpeg(inst.instanceId);

      // Auto-remove from MPD and rebuild
      setMessage?.(`Removing ${inst.camName} from MPD…`);
      try {
        await api.removeMpdFragment(evt, inst.instanceId);
        const masterInst = Object.values(grouped).flat().find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
        await api.rebuildMpd(evt, [], masterInst?.instanceId);
        await refreshFragStates(evt);
        if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
      } catch {}

      setMessage?.(`${inst.camName} stopped & removed from MPD.`);
      onRefresh?.();
    } catch (err) {
      setMessage?.(`Stop encoding error for ${inst.camName}: ${err.message}`);
    }
  };

  // --- MPD CAM TOGGLER LOGIC INTEGRATED ---
  // Helpers to detect cameras present in manifest.mpd
  function parseCamsInMpd(eventKey, manifestXml) {
    const cams = new Set();
    if (typeof manifestXml !== 'string' || !manifestXml.includes('<MPD')) return cams;
    try {
      const re = new RegExp(`/${eventKey}/([^/]+)/cam\\.mpd`, 'ig');
      let m;
      while ((m = re.exec(manifestXml)) !== null) {
        if (m[1]) cams.add(m[1].toLowerCase());
      }
    } catch {}
    try {
      const baseUrlRe = new RegExp(String.raw`<BaseURL>\s*(?:\.\.\/)?([^\s/<>]+)\/\s*<\/BaseURL>`, 'ig');
      let m;
      while ((m = baseUrlRe.exec(manifestXml)) !== null) {
        if (m[1]) cams.add(m[1].toLowerCase());
      }
    } catch {}
    try {
      const reId = /<AdaptationSet[^>]*\sid="([^"]+)"/ig;
      let m;
      while ((m = reId.exec(manifestXml)) !== null) {
        if (m[1]) cams.add(m[1].toLowerCase());
      }
    } catch {}
    return cams;
  }

  const miniIconBtn = (bg) => ({
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: '28px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    background: bg,
  });

  // Handlers to add/remove camera from MPD
  const handleAddCam = async (evt, inst, masterInst, manifestXml) => {
    const cam = inst.camName || inst.instanceId;
    const ok = window.confirm(`You are about to ADD the camera “${cam}” to the event.\nContinue?`);
    if (!ok) return;
    try {
      setMessage?.(`Adding ${cam} to the event...`);
      // 1) snapshot profile
      await authFetch(
        `${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/snapshot-cam-profiles`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cams: [{ camName: inst.camName, instanceId: inst.instanceId }] }),
        }
      );
      // 2) add fragment
      await api.addMpdFragment(evt, {
        instanceId: inst.instanceId,
        camName: inst.camName,
        role: (inst.role || '').toLowerCase() === 'audio' ? 'audio' : 'video',
      });
      // 3) rebuild + push all 3 MPD variants to master (handled server-side)
      await api.rebuildMpd(evt, [], masterInst?.instanceId);
      await refreshFragStates(evt);
      await onRefresh?.();
      await fetchManifestForEvent?.(evt, masterInst);
      setMessage?.(`Added ${cam} to the event`);
    } catch (e) {
      setMessage?.(`Error adding ${cam}: ${e.message}`);
    }
  };

  const handleRemoveCam = async (evt, inst, masterInst, manifestXml) => {
    const cam = inst.camName || inst.instanceId;
    const ok = window.confirm(`You are about to REMOVE the camera "${cam}" from the event.\nContinue?`);
    if (!ok) return;
    try {
      setMessage?.(`Removing ${cam} from the event...`);
      await api.removeMpdFragment(evt, inst.instanceId);
      // rebuild + push all 3 MPD variants to master (handled server-side)
      await api.rebuildMpd(evt, [], masterInst?.instanceId);
      await refreshFragStates(evt);
      await onRefresh?.();
      await fetchManifestForEvent?.(evt, masterInst);
      setMessage?.(`Removed ${cam} from the event`);
    } catch (e) {
      setMessage?.(`Error removing ${cam}: ${e.message}`);
    }
  };

  const handleToggleFragment = async (evt, camName, role, enabled, masterInst) => {
    try {
      setMessage?.(`${enabled ? 'Enabling' : 'Disabling'} ${role} for ${camName}...`);
      const key = `${evt}/${(camName || '').toLowerCase()}/${role}`;
      setFragStates(prev => ({ ...prev, [key]: enabled }));
      await api.toggleMpdFragment(evt, camName, role, enabled);
      await api.rebuildMpd(evt, [], masterInst?.instanceId);
      await refreshFragStates(evt);
      await fetchManifestForEvent?.(evt, masterInst);
      setMessage?.(`${role} for ${camName} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      await refreshFragStates(evt);
      setMessage?.(`Error toggling ${role} for ${camName}: ${e.message}`);
    }
  };

  // --- Server Config helpers ---
  const cfgLoadFrom = async (instanceId) => {
    setCfgLoading(true);
    try {
      if (cfgTab === 'srs') {
        const r = await api.getSrsConfig(instanceId);
        setCfgText(r.data?.config || '');
      } else {
        const r = await api.getControllerSource(instanceId);
        setCfgText(r.data?.source || '');
      }
    } catch (err) {
      const detail = err.response?.data?.message || err.message;
      setMessage?.(`Load failed: ${detail} — is the controller running on this instance?`);
    }
    setCfgLoading(false);
  };

  const cfgApplyAll = async (eventName) => {
    setCfgLoading(true);
    setCfgResults(null);
    setMessage?.(`Saving ${cfgTab === 'srs' ? 'SRS config' : 'controller source'} to all instances...`);
    try {
      const payload = { eventName };
      if (cfgTab === 'srs') payload.srsConfig = cfgText;
      else payload.controllerSource = cfgText;
      const r = await api.applyAll(payload);
      const results = r.data?.results || [];
      setCfgResults(results);
      const ok = results.filter(r => (r.srs === 'ok' || r.controller === 'ok')).length;
      // Controller edits are also published to the S3 dev channel — that's what
      // makes them survive service restarts on dev-channel instances (stable
      // keeps reading its own object and is never affected).
      const s3 = r.data?.s3DevChannel;
      const s3Note = cfgTab === 'controller'
        ? (s3 === 'ok' ? ' Published to S3 dev channel.' : s3 ? ` S3 dev publish FAILED: ${s3}` : '')
        : '';
      setMessage?.(`Saved to ${ok}/${results.length} instances.${s3Note} Reboot instances to apply.`);
    } catch (err) {
      const detail = err.response?.data?.message || err.message;
      setMessage?.(`Save failed: ${detail}`);
    }
    setCfgLoading(false);
  };

  // --- Pre-show / go-live: force every cam's source at once ---
  // Fired in parallel rather than in an await loop, so the skew between cameras is
  // one proxyTick (250ms) plus network jitter instead of the sum of N round-trips.
  // Each instance keeps classifying its camera's health while pinned to slate, so a
  // camera that has been publishing all through the pre-show is already 'ok' and
  // switches on the very next tick — no RECOVERY_MS wait. MIN_DWELL_MS doesn't bite
  // either: it only blocks a return to cam within 15s of the LAST switch, and a
  // pre-show is far longer than that.
  // Manual-return mode (opt-in): on ingest loss, each cam latches to the logo
  // and waits for the operator to press Go Live once the bitrate is stable,
  // instead of auto-recovering into a reconnecting cam's low-bitrate ramp.
  // State is per-controller; we track it optimistically per event here.
  const [manualReturnByEvent, setManualReturnByEvent] = useState({});
  const [manualReturnBusy, setManualReturnBusy] = useState(false);
  const loadManualReturnState = async (evt, cams) => {
    const first = cams?.[0];
    if (!first) return;
    try {
      const r = await api.getManualReturn(first.instanceId);
      setManualReturnByEvent(prev => ({ ...prev, [evt]: !!r.data?.manualReturn }));
    } catch { /* leave as-is; optimistic value stands */ }
  };
  const toggleManualReturn = async (evt, cams, enable) => {
    if (!cams?.length) return;
    setManualReturnBusy(true);
    setManualReturnByEvent(prev => ({ ...prev, [evt]: enable })); // optimistic
    const results = await Promise.allSettled(
      cams.map(inst => api.setManualReturn(inst.instanceId, enable))
    );
    const failed = results
      .map((r, i) => (r.status === 'rejected' ? (cams[i].camName || cams[i].instanceId) : null))
      .filter(Boolean);
    if (failed.length) {
      setMessage?.(`⚠ Manual-return toggle failed on ${failed.join(', ')} — mixed state, retry.`);
    } else {
      setMessage?.(enable
        ? `Manual return ON: on ingest loss cams hold the logo until you press Go Live.`
        : `Manual return OFF: cams auto-recover as before.`);
    }
    setManualReturnBusy(false);
  };

  const forceAllSources = async (source, cams) => {
    if (!cams?.length) return;
    setForcingSource(true);
    const results = await Promise.allSettled(
      cams.map(inst => api.setSourceOverride(inst.instanceId, source))
    );
    const failed = results
      .map((r, i) => (r.status === 'rejected' ? (cams[i].camName || cams[i].instanceId) : null))
      .filter(Boolean);
    if (failed.length) {
      // Partial application is the dangerous outcome: some cams live, some on logo.
      setMessage?.(`⚠ Source override FAILED on ${failed.join(', ')} — these cams are still on the previous source. Retry or switch them individually.`);
    } else {
      setMessage?.(source === 'slate'
        ? `All ${cams.length} cams switched to the logo.`
        : `All ${cams.length} cams switched to camera.`);
    }
    setForcingSource(false);
    onRefresh?.();
  };

  // --- Latency target helpers ---
  // `keepDraft` refreshes the panel without touching the input. latencyInfo only
  // changes on a manifest rebuild, so right after saving it still reports the
  // PREVIOUS request — refilling the field from it snapped the value the operator
  // had just typed back to the old one.
  const latencyLoad = async (evtName, { keepDraft = false } = {}) => {
    if (!evtName) return;
    try {
      const r = await api.getEvent(evtName);
      const d = r.data || {};
      setLatencyState({ targetMs: d.latencyTargetMs ?? null, info: d.latencyInfo || null });
      if (keepDraft) return;
      const secs = d.latencyInfo?.requestedMs != null
        ? d.latencyInfo.requestedMs / 1000
        : (d.latencyTargetMs != null ? d.latencyTargetMs / 1000 : '');
      setLatencyDraft(secs === '' ? '' : String(secs));
    } catch (e) {
      setMessage?.(`Could not read latency for "${evtName}": ${e.message}`);
    }
  };

  // Takes effect on the next manifest rebuild, and only lowers the live target on a
  // fresh session — mid-event the backend can raise it but never walk it back down,
  // because that repositions everyone already watching.
  const latencySave = async (evtName) => {
    const secs = latencyDraft === '' ? null : Number(latencyDraft);
    if (secs !== null && (!Number.isFinite(secs) || secs < 0)) {
      setMessage?.('Latency must be a positive number of seconds, or empty to clear.');
      return;
    }
    setLatencySaving(true);
    try {
      await api.setLiveDelay(evtName, secs);
      setMessage?.(secs === null
        ? `Latency target cleared for "${evtName}" — back to automatic.`
        : `Latency target set to ${secs}s for "${evtName}". Applies on the next manifest rebuild.`);
      await latencyLoad(evtName, { keepDraft: true });
    } catch (e) {
      setMessage?.(`Could not set latency: ${e.response?.data?.message || e.message}`);
    }
    setLatencySaving(false);
  };

  // --- Declared ingest helpers ---
  // Loads the saved profiles from Mongo (source of truth) plus the suggestion the
  // backend derives from the last run's MPD fragments, and asks each instance what
  // it currently has and measures. Drafts fall back saved → suggested → empty.
  const ingestLoad = async (evtName, cams) => {
    if (!evtName || !cams?.length) return;
    setIngestResult(null);

    let saved = {}, suggested = {};
    try {
      const r = await api.getIngestProfiles(evtName);
      saved     = r.data?.ingestProfiles || {};
      suggested = r.data?.suggested || {};
    } catch (e) {
      setMessage?.(`Could not read ingest profiles: ${e.message}`);
    }
    setIngestSuggested(suggested);

    const drafts = {};
    for (const cam of cams) {
      const src = saved[cam.camName] || suggested[cam.camName] || {};
      drafts[cam.camName] = {
        width:  src.width  ?? '',
        height: src.height ?? '',
        codec:  src.codec  || 'hevc',
        kbps:   src.kbps   ?? '',
        offsetSeconds: src.offsetSeconds ?? '',
      };
    }
    setIngestDrafts(drafts);

    // Live state per instance: best-effort, an unreachable controller is not fatal.
    const rows = await Promise.all(cams.map(async (cam) => {
      try {
        const r = await api.getIngestConfig(cam.instanceId);
        return [cam.instanceId, r.data];
      } catch (err) {
        return [cam.instanceId, { error: err.response?.data?.message || err.message }];
      }
    }));
    setIngestState(Object.fromEntries(rows));
  };

  // Saves to Mongo and pushes to the instance in one go — same convention as
  // saveEventCmd + updateNodeScript. Mongo is authoritative; the push just makes it
  // take effect at the next Prepare without waiting for a full event start.
  const ingestSaveCam = async (evtName, cam) => {
    const draft = ingestDrafts[cam.camName];
    if (!draft) return;
    setIngestSaving(true);
    setIngestResult(null);
    // '' clears a field, dropping it back to the measured/default path.
    const payload = {
      width:  draft.width  === '' ? '' : Number(draft.width),
      height: draft.height === '' ? '' : Number(draft.height),
      codec:  draft.codec  || '',
      kbps:   draft.kbps   === '' ? '' : Number(draft.kbps),
      // Declared signal latency (persistent, applied as base on every rebuild)
      offsetSeconds: (draft.offsetSeconds === '' || draft.offsetSeconds == null) ? '' : Number(draft.offsetSeconds),
    };
    try {
      await api.saveIngestProfile(evtName, cam.camName, payload);
    } catch (err) {
      const data = err.response?.data;
      setIngestResult([{ name: cam.camName, ok: false, error: data?.errors?.join('; ') || data?.message || err.message }]);
      setIngestSaving(false);
      return;
    }
    let pushed = true, pushErr = null;
    try {
      await api.setIngestConfig(cam.instanceId, payload);
    } catch (err) {
      pushed = false;
      pushErr = err.response?.data?.errors?.join('; ') || err.response?.data?.message || err.message;
    }
    setIngestResult([{
      name: cam.camName, ok: true,
      error: pushed ? null : `saved in DB, not pushed to instance (${pushErr}) — will apply at event start`,
    }]);
    setMessage?.(`Ingest profile saved for ${cam.camName}${pushed ? ' and pushed to its instance' : ' (DB only)'}.`);
    await ingestLoad(evtName, [cam]);
    setIngestSaving(false);
  };

  const ingestApplySuggested = (cams) => {
    setIngestDrafts(prev => {
      const next = { ...prev };
      for (const cam of cams) {
        const s = ingestSuggested[cam.camName];
        if (!s) continue;
        next[cam.camName] = {
          width:  s.width  ?? '',
          height: s.height ?? '',
          codec:  s.codec  || 'hevc',
          kbps:   s.kbps   ?? '',
        };
      }
      return next;
    });
  };

  const setIngestField = (camName, field, value) =>
    setIngestDrafts(prev => ({ ...prev, [camName]: { ...(prev[camName] || {}), [field]: value } }));

  const ingestInputStyle = {
    width: 84, fontSize: 12, padding: '4px 6px', borderRadius: 4,
    color: 'inherit', background: 'rgba(0,0,0,0.25)', border: '1px solid #555',
  };

  const cfgRebootAll = async (eventName) => {
    if (!window.confirm(`Reboot ALL instances for "${eventName}"?\n\nThis will restart SRS, ffmpeg-controller, and any running FFmpeg encoders.`)) return;
    setCfgLoading(true);
    setMessage?.(`Rebooting instances for "${eventName}"...`);
    try {
      const r = await api.rebootInstances(eventName);
      const rebooted = r.data?.rebooted || [];
      setMessage?.(`Rebooted ${rebooted.length} instances: ${rebooted.map(i => i.camName).join(', ')}. They will be back in ~30s.`);
    } catch (err) {
      const detail = err.response?.data?.message || err.message;
      setMessage?.(`Reboot failed: ${detail}`);
    }
    setCfgLoading(false);
  };

  return (
    <div
      style={{ marginTop: 12 }}
      className={
        document.body.classList.contains('studio-mode')
          ? 'MinimalDashboard studio-minimal'
          : 'MinimalDashboard'
      }
    >
      {/* Modal panel to select FFmpeg preset per camera */}
      {showPresetPanel && selectedInstance && (
        <StudioCamPresetPanel
          instance={selectedInstance}
          presets={presets}
          currentCmd={currentCmd}
          onApplyPreset={handleApplyPreset}
          onClose={() => { setShowPresetPanel(false); setSelectedCamInstanceId(null); }}
          loading={presetPanelLoading}
        />
      )}

      {Object.entries(grouped).map(([evt, list]) => {
        const pendingForEvt = pendingLaunchesByEvent?.[evt] || [];
        const pendingMaster = pendingForEvt.some(
          (p) => (p.role || '').toLowerCase() === 'master'
        );

        const masterInst = list.find((i) => (i.role || '').toLowerCase() === 'master');
        const camsOnly = list.filter((i) => (i.role || '').toLowerCase() !== 'master');

        const eventStarted = camsOnly.some(c => !!ffmpegStat[c.instanceId])
          || camsOnly.some(c => isInGrace(c.instanceId));

        const selectedCount = camsOnly.filter(
          (inst) => !!rtmpOk[inst.instanceId] && startSelection[inst.instanceId]
        ).length;
        const eligibleCount = selectedCount;

        const hasAnyPending = pendingForEvt.length > 0;

        return (
          <div
            key={evt}
            style={{ margin: '16px 0', border: '1px solid #ddd', borderRadius: 8, padding: 12 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
                flexWrap: 'wrap',
              }}
            >
              <strong style={{ fontSize: 20 }}>Event: {evt}</strong>

              {masterInst ? (
                <>
                  <Badge
                    ok={eventStarted}
                    on={COLORS.ok}
                    off={COLORS.err}
                    labelOk="Running"
                    labelOff="Stopped"
                  />
                </>
              ) : pendingMaster ? (
                <Pill color={COLORS.warn}>Deploying master…</Pill>
              ) : camsOnly.length > 0 ? (
                // Cams exist but no master: a partially-failed launch (e.g. AZ
                // capacity) leaves the event unstartable with no way to recover
                // from the UI — relaunch just the master.
                <button
                  onClick={async () => {
                    try {
                      setMessage?.(`Launching master for "${evt}"…`);
                      const r = await api.launchMaster(evt);
                      if (!r.data?.success) throw new Error(r.data?.message || 'launch failed');
                      setMessage?.(`Master launching for "${evt}" — it will appear in the list shortly.`);
                      onRefresh?.();
                    } catch (e) {
                      setMessage?.(`Master launch failed: ${e.response?.data?.message || e.message}`);
                    }
                  }}
                  style={{
                    background: COLORS.warn, color: '#fff', border: 'none',
                    borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12,
                  }}
                  title="This event has cameras but no master instance (a previous launch partially failed). Launch only the master."
                >
                  ⚠ No master — Launch master
                </button>
              ) : null}

              {hasAnyPending && (
                <span style={{ marginLeft: 8 }}>
                  <Pill color={COLORS.idle}>Launching…</Pill>
                </span>
              )}

              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  disabled={!masterInst || eligibleCount === 0 || eventStarted || hasAnyPending || startingEvent}
                  onClick={() => autoStartEvent(evt, list)}
                  title={
                    startingEvent
                      ? 'Startup in progress — use Abort to cancel'
                      : eventStarted
                      ? 'Event already running'
                      : hasAnyPending
                      ? 'Wait for instances to finish launching'
                      : ''
                  }
                  style={{
                    background:
                      !masterInst || eligibleCount === 0 || eventStarted || hasAnyPending || startingEvent
                        ? '#9e9e9e'
                        : '#3f51b5',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 10px',
                    cursor:
                      !masterInst || eligibleCount === 0 || eventStarted || hasAnyPending || startingEvent
                        ? 'not-allowed'
                        : 'pointer',
                  }}
                >
                  ▶ Start Event ({selectedCount} cams)
                </button>

                {/* Pre-show / go-live. Pinning every cam to the logo while the
                    cameras keep publishing is the clean way to run a pre-show: sizing
                    stays measured, the ASTs stay aligned, and going live costs each
                    cam exactly one switch, taken at the same moment. */}
                {(() => {
                  const forcedCount = camsOnly.filter(
                    (i) => sourceOverrideMap[i.instanceId]?.forcedSource === 'slate'
                  ).length;
                  // Only "everyone on the logo" offers Go Live. A mixed state means a
                  // previous all-cams call didn't land everywhere, so the action stays
                  // "put everyone on the logo" — pressing again retries it and gets the
                  // event back to one consistent source. The count makes that visible.
                  const allLogo = camsOnly.length > 0 && forcedCount === camsOnly.length;
                  const mixed   = forcedCount > 0 && !allLogo;
                  const busy    = forcingSource || !camsOnly.length;
                  const label   = forcingSource ? 'Switching…'
                                : allLogo       ? 'Go Live (all cams)'
                                : mixed         ? `Logo (${forcedCount}/${camsOnly.length})`
                                :                 'Logo (all cams)';
                  return (
                    <button
                      disabled={busy}
                      onClick={() => forceAllSources(allLogo ? 'auto' : 'slate', camsOnly)}
                      title={allLogo
                        ? 'Release the override on every camera at once — all cut to CAM on their next tick'
                        : 'Pin every camera to the logo (pre-show). Cameras keep publishing underneath.'}
                      style={{
                        background: busy ? '#9e9e9e' : allLogo ? '#4caf50' : '#9a51e2',
                        color: '#fff', border: 'none', borderRadius: 4, padding: '6px 10px',
                        cursor: busy ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  );
                })()}

                {startingEvent && (
                  <button
                    onClick={() => { startAbortRef.current = true; }}
                    style={{
                      background: '#f44336',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                    }}
                  >
                    ✕ Abort Start
                  </button>
                )}

                <button
                  disabled={!masterInst}
                  onClick={async () => {
                    try {
                      const ok = window.confirm(
                        `Are you sure you want to stop the event "${evt}"?\n\n` +
                          `This will stop FFmpeg encoding on all cameras.`
                      );
                      if (!ok) return;

                      setMessage?.(`Stopping all encoders for "${evt}"…`);
                      await Promise.all(
                        camsOnly.map(async (inst) => {
                          try {
                            await api.stopFfmpeg(inst.instanceId);
                          } catch {}
                        })
                      );

                      // Stop publishTime updater on master
                      if (masterInst) {
                        try { await api.publishStop(masterInst.instanceId); } catch {}
                      }

                      // Generate VOD manifest (static, multi-Period)
                      try {
                        setMessage?.('Generating VOD manifest…');
                        const { data } = await api.generateVodMpd(evt, masterInst?.instanceId);
                        if (data?.success) {
                          const nPeriods = data.periods?.length || 0;
                          setMessage?.(`VOD manifest generated (${nPeriods} period${nPeriods !== 1 ? 's' : ''}, ${data.duration}).`);
                        }
                      } catch (e) {
                        setMessage?.(`Warning: VOD manifest generation failed: ${e.message}`);
                      }

                      onRefresh?.();
                      setMessage?.('Event stopped');
                    } catch (err) {
                      setMessage?.(`Stop event error: ${err.message}`);
                    }
                  }}
                  style={{
                    background: !masterInst ? '#9e9e9e' : COLORS.err,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 10px',
                    cursor: !masterInst ? 'not-allowed' : 'pointer',
                  }}
                >
                  ⏹ Stop Event
                </button>

                <button
                  onClick={() => terminateAllForEvent(evt, list)}
                  style={{
                    background: COLORS.err,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 10px',
                    cursor: 'pointer',
                  }}
                  title="Terminate master and all instances in this event"
                >
                  ✖ Terminate All
                </button>
              </div>
            </div>

            {/* Studio launch path — DASH (existing pipeline) vs LiveKit (WHIP,
                parallel, no slate/warmup/drift — see LivekitEventPanel) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: '#888' }}>Path:</span>
              {['dash', 'livekit'].map(m => (
                <button
                  key={m}
                  onClick={() => setEventMode(evt, m)}
                  style={{
                    padding: '4px 12px', borderRadius: 4, fontSize: 12,
                    background: (streamingModeByEvent[evt] || 'dash') === m ? '#9a51e2' : 'transparent',
                    color: (streamingModeByEvent[evt] || 'dash') === m ? '#fff' : 'inherit',
                    border: '1px solid #9a51e2', cursor: 'pointer',
                  }}
                >
                  {m === 'dash' ? 'DASH' : 'LiveKit (WHIP)'}
                </button>
              ))}
            </div>

            {streamingModeByEvent[evt] === 'livekit' ? (
              <LivekitEventPanel
                evt={evt}
                camsOnly={camsOnly}
                presets={presets}
                api={api}
                ingressMap={livekitIngressByEvent[evt]}
                participantsMap={livekitParticipantsByEvent[evt]}
                onRefreshIngress={() => Promise.all([refreshLivekitIngress(evt), refreshLivekitParticipants(evt)])}
                setMessage={setMessage}
              />
            ) : (
            <>

            {/* Server Config collapsible panel (advanced mode) */}
            {advancedMode && (
              <div style={{ marginBottom: 10, border: '1px solid #555', borderRadius: 8, overflow: 'hidden' }}>
                <div
                  onClick={() => {
                    setCfgOpen(o => {
                      const opening = !o;
                      if (opening && starvedSwitchByEvt[evt] === undefined) loadFailoverState(evt, camsOnly);
                      if (opening && manualReturnByEvent[evt] === undefined) loadManualReturnState(evt, camsOnly);
                      if (opening && normQualByEvt[evt] === undefined) loadNormalizerQuality(evt);
                      return opening;
                    });
                  }}
                  style={{
                    padding: '8px 12px', background: 'rgba(128,128,128,0.15)', cursor: 'pointer',
                    fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8,
                    color: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 12 }}>{cfgOpen ? '\u25BC' : '\u25B6'}</span>
                  Server Config
                </div>
                {cfgOpen && (
                  <div style={{ padding: 12 }}>
                    {/* Failover behavior: hard ingest loss ALWAYS fails over to the
                        logo; the delivery-ratio ("starved") analysis is opt-in
                        because it proved too trigger-happy on long lives, and
                        each switch damages the DASH timeline. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #555', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 'bold' }}>Quality failover (starved detection):</span>
                      <button
                        disabled={cfgLoading || !camsOnly.length}
                        onClick={() => {
                          // Unknown state (never loaded / load failed): fetch it \u2014
                          // a blind click must not silently ENABLE the analysis.
                          if (typeof starvedSwitchByEvt[evt] !== 'boolean') loadFailoverState(evt, camsOnly);
                          else toggleFailover(evt, camsOnly);
                        }}
                        style={{
                          padding: '4px 14px', borderRadius: 4, border: 'none', cursor: cfgLoading ? 'not-allowed' : 'pointer',
                          background: starvedSwitchByEvt[evt] === true ? '#ff9800'
                            : starvedSwitchByEvt[evt] === false ? '#4caf50' : '#9e9e9e',
                          color: '#fff', fontWeight: 'bold', fontSize: 12,
                        }}
                      >
                        {starvedSwitchByEvt[evt] === true ? 'ON \u2014 slow delivery also switches to logo'
                          : starvedSwitchByEvt[evt] === false ? 'OFF \u2014 only real ingest loss switches'
                          : 'load state\u2026'}
                      </button>
                      <span style={{ fontSize: 11, color: '#888', maxWidth: 480 }}>
                        Default OFF. Applies to all cams of this event; resets to OFF when an instance restarts.
                      </span>
                    </div>

                    {/* Manual return: on ingest loss, hold the logo and wait for
                        the operator to Go Live once the bitrate is stable,
                        instead of auto-recovering into a reconnecting cam's
                        low-bitrate ramp. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #555', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 'bold' }}>Manual return on ingest loss:</span>
                      <button
                        disabled={manualReturnBusy || !camsOnly.length}
                        onClick={() => toggleManualReturn(evt, camsOnly, !manualReturnByEvent[evt])}
                        style={{
                          padding: '4px 14px', borderRadius: 4, border: 'none', cursor: manualReturnBusy ? 'not-allowed' : 'pointer',
                          background: manualReturnByEvent[evt] ? '#ff9800' : '#4caf50',
                          color: '#fff', fontWeight: 'bold', fontSize: 12,
                        }}
                      >
                        {manualReturnByEvent[evt] ? 'ON — holds logo until you Go Live' : 'OFF — auto-recovers'}
                      </button>
                      <span style={{ fontSize: 11, color: '#888', maxWidth: 480 }}>
                        Default OFF. On ingest loss each cam holds the logo; press the camera's source button (Go Live) when its bitrate is stable. Resets to OFF on instance restart.
                      </span>
                    </div>

                    {/* Normalizer quality: intermediate 8K re-encode quality vs
                        NVENC headroom. Default p4 + temporal AQ = best quality,
                        ~52% of the encoder — lowering it is what frees room for
                        a higher-preset LiveKit leg on dual events. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #555', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 'bold' }}>Normalizer quality:</span>
                      {(() => {
                        const cur = normQualByEvt[evt] || {};
                        const upd = (patch) => {
                          const next = { ...cur, ...patch };
                          for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
                          saveNormalizerQuality(evt, Object.keys(next).length ? next : null);
                        };
                        const selStyle = { fontSize: 12, padding: 3, background: '#222', color: '#fff', border: '1px solid #555', borderRadius: 4 };
                        return (
                          <>
                            <select value={cur.preset || ''} disabled={cfgLoading} style={selStyle}
                              onChange={e => upd({ preset: e.target.value || undefined })}>
                              <option value="">preset: default (p4)</option>
                              {/* p5-p7 removed: measured 2026-09-18 — p5 on the
                                  8K normalizer can't even reach 0.5x realtime. */}
                              {['p1','p2','p3','p4'].map(p => (
                                <option key={p} value={p}>preset: {p}</option>
                              ))}
                            </select>
                            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input type="checkbox" disabled={cfgLoading}
                                checked={cur.temporalAq !== false}
                                onChange={e => upd({ temporalAq: e.target.checked })} />
                              temporal AQ
                            </label>
                            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input type="checkbox" disabled={cfgLoading}
                                checked={cur.spatialAq !== false}
                                onChange={e => upd({ spatialAq: e.target.checked })} />
                              spatial AQ
                            </label>
                            <label style={{ fontSize: 12 }} title="AQ strength 1-15 (default 8)">
                              AQ str{' '}
                              <input type="number" min="1" max="15" disabled={cfgLoading}
                                defaultValue={cur.aqStrength ?? ''}
                                placeholder="8"
                                onBlur={e => upd({ aqStrength: e.target.value ? Number(e.target.value) : undefined })}
                                style={{ ...selStyle, width: 48 }} />
                            </label>
                            <label style={{ fontSize: 12 }} title="Fixed bitrate in Mbps. Empty = automatic (input × 1.2, capped per resolution tier).">
                              Mbps{' '}
                              <input type="number" min="1" max="60" disabled={cfgLoading}
                                defaultValue={cur.bitrateMbps ?? ''}
                                placeholder="auto"
                                onBlur={e => upd({ bitrateMbps: e.target.value ? Number(e.target.value) : undefined })}
                                style={{ ...selStyle, width: 58 }} />
                            </label>
                          </>
                        );
                      })()}
                      <span style={{ fontSize: 11, color: '#888', maxWidth: 460 }}>
                        Applies at the NEXT event start (no live restart). Lower preset / AQ off frees NVENC for a higher-quality LiveKit encode on dual events. Bitrate empty = auto (input × 1.2).
                      </span>
                    </div>
                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                      {['srs', 'controller'].map(tab => (
                        <button
                          key={tab}
                          onClick={() => { setCfgTab(tab); setCfgText(''); setCfgResults(null); }}
                          style={{
                            padding: '4px 12px', borderRadius: 4,
                            background: cfgTab === tab ? '#9a51e2' : 'transparent',
                            color: cfgTab === tab ? '#fff' : 'inherit',
                            border: cfgTab === tab ? '1px solid #9a51e2' : '1px solid #888',
                            cursor: 'pointer', fontWeight: cfgTab === tab ? 'bold' : 'normal',
                          }}
                        >
                          {tab === 'srs' ? 'SRS Config' : 'Controller (index.js)'}
                        </button>
                      ))}
                    </div>

                    {/* Load from instance */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, color: 'inherit' }}>Load from:</span>
                      {camsOnly
                        .slice(0, 8)
                        .map(inst => (
                          <button
                            key={inst.instanceId}
                            disabled={cfgLoading}
                            onClick={() => cfgLoadFrom(inst.instanceId)}
                            style={{
                              padding: '2px 8px', border: '1px solid #888', borderRadius: 4,
                              background: 'transparent', color: 'inherit',
                              cursor: cfgLoading ? 'not-allowed' : 'pointer', fontSize: 12,
                            }}
                          >
                            {inst.camName || inst.instanceId}
                          </button>
                        ))}
                    </div>

                    {/* Editor — collapsed until something is loaded: an empty
                        300px textarea per event ate the whole screen for nothing. */}
                    {!cfgText ? (
                      <div style={{ fontSize: 12, color: '#888', padding: '8px 0' }}>
                        Select a camera above to load its {cfgTab === 'srs' ? 'SRS config' : 'controller source'}.
                      </div>
                    ) : (
                    <>
                    <textarea
                      value={cfgText}
                      onChange={e => setCfgText(e.target.value)}
                      readOnly={cfgTab === 'controller'}
                      style={{
                        width: '100%', minHeight: 300, fontFamily: 'monospace', fontSize: 12,
                        borderRadius: 4, padding: 8, boxSizing: 'border-box',
                        resize: 'vertical', color: 'inherit', background: 'rgba(0,0,0,0.25)',
                        border: '1px solid #555',
                      }}
                      spellCheck={false}
                    />

                    {/* Save + Reboot buttons. No Save for the controller tab: the
                        controller is deployed from S3 (stable/dev channels) and
                        re-downloaded on every service restart, so an edit pushed
                        from here would not survive — the tab is view-only. */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {cfgTab === 'srs' ? (
                        <button
                          disabled={cfgLoading || !cfgText.trim()}
                          onClick={() => cfgApplyAll(evt)}
                          style={{
                            padding: '6px 14px',
                            color: '#fff', border: 'none', borderRadius: 4,
                            cursor: cfgLoading || !cfgText.trim() ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {cfgLoading ? 'Saving...' : 'Save to All'}
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: '#888' }}>
                          Read-only — the controller deploys from S3 (stable/dev channel), local edits don't survive a restart.
                        </span>
                      )}
                      <button
                        disabled={cfgLoading}
                        onClick={() => cfgRebootAll(evt)}
                        style={{
                          padding: '6px 14px',
                          background: cfgLoading ? '#9e9e9e' : '#f44336',
                          color: '#fff', border: 'none', borderRadius: 4,
                          cursor: cfgLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Reboot Instances
                      </button>
                    </div>
                    </>
                    )}

                    {/* Results */}
                    {cfgResults && (
                      <div style={{ marginTop: 8, fontSize: 13, fontFamily: 'monospace' }}>
                        {cfgResults.map((r, i) => {
                          const ok = r.srs === 'ok' || r.controller === 'ok';
                          return (
                            <div key={i} style={{ padding: '2px 0', color: ok ? '#4caf50' : '#f44336' }}>
                              {ok ? '\u2713' : '\u2717'}{' '}
                              {r.camName || r.instanceId}
                              {r.srs && ` \u2014 SRS: ${r.srs}`}
                              {r.controller && ` \u2014 Controller: ${r.controller}`}
                              {r.error && ` \u2014 ${r.error}`}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Slate Logo Upload */}
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #555' }}>
                      <h4 style={{ margin: '0 0 8px', fontSize: 14, color: 'inherit' }}>Slate Logo</h4>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = () => {
                              const base64 = reader.result.split(',')[1];
                              setSlateLogoBase64(base64);
                              setSlateLogoName(file.name);
                            };
                            reader.readAsDataURL(file);
                          }}
                          style={{ fontSize: 12, color: 'inherit' }}
                        />
                        {slateLogoName && (
                          <span style={{ fontSize: 12, color: '#4caf50' }}>{slateLogoName}</span>
                        )}
                        <button
                          disabled={!slateLogoBase64 || slateUploading}
                          onClick={async () => {
                            setSlateUploading(true);
                            setSlateResult(null);
                            try {
                              const results = await Promise.allSettled(
                                camsOnly.map(async (cam) => {
                                  await api.uploadSlateLogo(cam.instanceId, slateLogoBase64);
                                  await api.restartSlate(cam.instanceId);
                                  return cam.camName || cam.instanceId;
                                })
                              );
                              setSlateResult(results.map((r, idx) =>
                                r.status === 'fulfilled'
                                  ? { name: r.value, ok: true }
                                  : { name: camsOnly[idx]?.camName || camsOnly[idx]?.instanceId, ok: false, error: r.reason?.message }
                              ));
                            } catch (err) {
                              setSlateResult([{ name: 'Error', ok: false, error: err.message }]);
                            } finally {
                              setSlateUploading(false);
                            }
                          }}
                          style={{
                            padding: '6px 14px', background: slateUploading ? '#9e9e9e' : '#9a51e2',
                            color: '#fff', border: 'none', borderRadius: 4,
                            cursor: !slateLogoBase64 || slateUploading ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {slateUploading ? 'Uploading...' : 'Upload & Restart All'}
                        </button>
                      </div>
                      {slateResult && (
                        <div style={{ marginTop: 6, fontSize: 12, fontFamily: 'monospace' }}>
                          {slateResult.map((r, i) => (
                            <div key={i} style={{ color: r.ok ? '#4caf50' : '#f44336' }}>
                              {r.ok ? '\u2713' : '\u2717'} {r.name} {r.error ? `\u2014 ${r.error}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Latency target */}
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #555' }}>
                      <h4 style={{ margin: '0 0 4px', fontSize: 14, color: 'inherit' }}>Latency</h4>
                      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 8, lineHeight: 1.4 }}>
                        How far behind the live edge the player sits. Must stay above the largest
                        camera offset, or that camera is positioned past its own edge and its
                        retries stall the others. The backend raises it on its own when the offsets
                        need it, and only lowers it on a fresh session — never under a live audience.
                        Leave empty for automatic.
                      </div>

                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          disabled={latencySaving}
                          onClick={() => latencyLoad(evt)}
                          style={{
                            padding: '6px 14px', color: '#fff', border: 'none', borderRadius: 4,
                            cursor: latencySaving ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Load
                        </button>
                        <label style={{ fontSize: 12 }}>
                          Target{' '}
                          <input
                            type="number" min="0" step="0.1" value={latencyDraft}
                            onChange={e => setLatencyDraft(e.target.value)}
                            placeholder="auto" style={ingestInputStyle}
                          />
                          {' '}s
                        </label>
                        <button
                          disabled={latencySaving}
                          onClick={() => latencySave(evt)}
                          style={{
                            padding: '6px 14px', background: latencySaving ? '#9e9e9e' : '#9a51e2',
                            color: '#fff', border: 'none', borderRadius: 4,
                            cursor: latencySaving ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {latencySaving ? 'Saving…' : 'Set'}
                        </button>
                        {/* "Set" alone can only ever raise the target: the pin blocks
                            lowering so a live audience is never repositioned by surprise.
                            Lowering therefore needs an explicit unpin, which is this. */}
                        <button
                          disabled={latencySaving}
                          onClick={async () => {
                            const masterInst = Object.values(grouped).flat()
                              .find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
                            if (!confirmDanger(
                              `Unpin the latency target for "${evt}" and rebuild the manifest?\n\n` +
                              `It will be recomputed from the current offsets, so it can DROP — ` +
                              `every connected viewer repositions. Safe between takes, disruptive on air.`
                            )) return;
                            setLatencySaving(true);
                            try {
                              await api.rebuildMpd(evt, [], masterInst?.instanceId, { resetLatencyTarget: true });
                              setMessage?.(`Latency target unpinned and manifest rebuilt for "${evt}".`);
                              await latencyLoad(evt);
                            } catch (e) {
                              setMessage?.(`Unpin failed: ${e.response?.data?.message || e.message}`);
                            } finally {
                              setLatencySaving(false);
                            }
                          }}
                          title="Clear the pinned target and rebuild — the only way to lower it without restarting the session"
                          style={{
                            padding: '6px 14px', background: latencySaving ? '#9e9e9e' : COLORS.warn,
                            color: '#fff', border: 'none', borderRadius: 4,
                            cursor: latencySaving ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Unpin &amp; rebuild
                        </button>
                      </div>

                      {latencyState && (
                        <div style={{ marginTop: 8, fontSize: 12, fontFamily: 'monospace', opacity: 0.8 }}>
                          {latencyState.targetMs == null ? (
                            <div>Not computed yet — start the event once, then Load.</div>
                          ) : (
                            <>
                              <div>
                                in manifest: {(latencyState.targetMs / 1000).toFixed(1)}s
                                {/* Two different reasons the target can exceed the request, and
                                    blaming the offsets for both sends you hunting for an offset
                                    that isn't there. If the safe floor already covers the request,
                                    the excess is a pin carried from earlier in the session — and a
                                    pin only ever rises, so lowering liveDelay now changes nothing
                                    until the session is reset. */}
                                {latencyState.info?.requestedMs != null &&
                                  latencyState.info.requestedMs !== latencyState.targetMs &&
                                  (latencyState.info.recommendedMs > latencyState.info.requestedMs
                                    ? ` (you asked for ${(latencyState.info.requestedMs / 1000).toFixed(1)}s — raised to cover the offsets)`
                                    : ` (you asked for ${(latencyState.info.requestedMs / 1000).toFixed(1)}s — held by the pinned value; reset the session to lower it)`)}
                              </div>
                              {latencyState.info && (
                                <>
                                  <div>
                                    worst offset: {(latencyState.info.worstOffsetMs / 1000).toFixed(2)}s
                                    {latencyState.info.worstOffsetCam ? ` on ${latencyState.info.worstOffsetCam}` : ''}
                                  </div>
                                  <div style={{
                                    color: latencyState.info.recommendedMs < latencyState.targetMs ? '#4caf50' : 'inherit'
                                  }}>
                                    safe floor: {(latencyState.info.recommendedMs / 1000).toFixed(1)}s
                                    {latencyState.info.recommendedMs < latencyState.targetMs &&
                                      ` — you could cut ~${((latencyState.targetMs - latencyState.info.recommendedMs) / 1000).toFixed(1)}s`}
                                  </div>
                                  {/* The target is pinned per session and only ever rises, so a value
                                      computed before this session started is carried over — say when,
                                      or a stale pin is indistinguishable from a fresh one. */}
                                  {latencyState.info.computedAt && (() => {
                                    const when = new Date(latencyState.info.computedAt);
                                    const stale = (Date.now() - when.getTime()) > 6 * 60 * 60 * 1000;
                                    return (
                                      <div style={{ color: stale ? COLORS.warn : 'inherit' }}>
                                        pinned at: {when.toLocaleString()}
                                        {stale && ' — older than this session? reset to recompute'}
                                      </div>
                                    );
                                  })()}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Declared ingest profiles (per camera) */}
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #555' }}>
                      <h4 style={{ margin: '0 0 4px', fontSize: 14, color: 'inherit' }}>Ingest (declared)</h4>
                      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 8, lineHeight: 1.4 }}>
                        Each rig's real parameters. Used to size slate + normalizer <b>before</b> the
                        camera publishes, which is what lets an event start on the logo alone. A live
                        measurement always wins over these. Stored per camera in the DB and pushed to
                        the instance at event start, so a relaunched instance re-seeds itself.
                        Applied at the next <b>Prepare</b> \u2014 saving never restarts anything mid-event.
                      </div>

                      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          disabled={ingestSaving || !camsOnly.length}
                          onClick={() => ingestLoad(evt, camsOnly)}
                          style={{
                            padding: '6px 14px', color: '#fff', border: 'none', borderRadius: 4,
                            cursor: ingestSaving || !camsOnly.length ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Load
                        </button>
                        <button
                          disabled={ingestSaving || !Object.keys(ingestSuggested).length}
                          onClick={() => ingestApplySuggested(camsOnly)}
                          title="Fill the fields from what the last run's manifest reported for each cam"
                          style={{
                            padding: '6px 14px', background: 'transparent', color: 'inherit',
                            border: '1px solid #777', borderRadius: 4,
                            cursor: ingestSaving || !Object.keys(ingestSuggested).length ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Use last run's values
                        </button>
                      </div>

                      {!camsOnly.length && (
                        <div style={{ fontSize: 12, opacity: 0.6 }}>No cameras in this event.</div>
                      )}

                      {camsOnly.map((cam) => {
                        const draft = ingestDrafts[cam.camName] || { width: '', height: '', codec: 'hevc', kbps: '' };
                        const sug   = ingestSuggested[cam.camName];
                        const live  = ingestState[cam.instanceId];
                        const eff   = live?.effective || {};
                        return (
                          <div key={cam.instanceId} style={{ padding: '8px 0', borderTop: '1px solid #444' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                              {cam.camName || cam.instanceId}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <label style={{ fontSize: 12 }}>
                                W{' '}
                                <input
                                  type="number" min="1" value={draft.width}
                                  onChange={e => setIngestField(cam.camName, 'width', e.target.value)}
                                  placeholder="7680" style={ingestInputStyle}
                                />
                              </label>
                              <label style={{ fontSize: 12 }}>
                                H{' '}
                                <input
                                  type="number" min="1" value={draft.height}
                                  onChange={e => setIngestField(cam.camName, 'height', e.target.value)}
                                  placeholder="3840" style={ingestInputStyle}
                                />
                              </label>
                              <label style={{ fontSize: 12 }}>
                                Codec{' '}
                                <select
                                  value={draft.codec}
                                  onChange={e => setIngestField(cam.camName, 'codec', e.target.value)}
                                  style={ingestInputStyle}
                                >
                                  <option value="hevc">hevc</option>
                                  <option value="h264">h264</option>
                                </select>
                              </label>
                              <label style={{ fontSize: 12 }}>
                                Kbps{' '}
                                <input
                                  type="number" min="1" value={draft.kbps}
                                  onChange={e => setIngestField(cam.camName, 'kbps', e.target.value)}
                                  placeholder="35000" style={ingestInputStyle}
                                />
                              </label>
                              <label
                                style={{ fontSize: 12 }}
                                title="Inherent signal latency of this rig, in seconds. Positive = delay this camera to line up with slower transports (e.g. an SRT feed arriving 3s ahead of an RTMP one gets +3). Persists across sessions, unlike the drift-panel offsets."
                              >
                                Signal offset (s){' '}
                                <input
                                  type="number" step="0.1" min="-30" max="30" value={draft.offsetSeconds ?? ''}
                                  onChange={e => setIngestField(cam.camName, 'offsetSeconds', e.target.value)}
                                  placeholder="0" style={ingestInputStyle}
                                />
                              </label>
                              <button
                                disabled={ingestSaving}
                                onClick={() => ingestSaveCam(evt, cam)}
                                style={{
                                  padding: '5px 14px', background: ingestSaving ? '#9e9e9e' : '#9a51e2',
                                  color: '#fff', border: 'none', borderRadius: 4, fontSize: 12,
                                  cursor: ingestSaving ? 'not-allowed' : 'pointer',
                                }}
                              >
                                Save
                              </button>
                            </div>

                            <div style={{ fontSize: 11, fontFamily: 'monospace', opacity: 0.7, marginTop: 4, paddingLeft: 2 }}>
                              {sug && (
                                <div>
                                  last run: {sug.width}x{sug.height} {sug.codec || '?'} {sug.kbps ? `${sug.kbps}kbps` : ''}
                                </div>
                              )}
                              {live?.error && <div style={{ color: '#f44336' }}>instance: {live.error}</div>}
                              {live && !live.error && (
                                <>
                                  <div>
                                    measured: {live.measured
                                      ? `${live.measured.w}x${live.measured.h} ${live.measured.codec} ${live.measured.kbps}kbps`
                                      : 'not publishing'}
                                  </div>
                                  <div>
                                    effective: {eff.w || '?'}x{eff.h || '?'} {eff.codec || '?'} {eff.kbps || 0}kbps
                                    {eff.from && ` (res ${eff.from.resolution}, codec ${eff.from.codec}, kbps ${eff.from.kbps})`}
                                  </div>
                                </>
                              )}
                              {live?.mismatch && (
                                <div style={{ color: live.mismatch.breaking ? '#f44336' : '#ff9800' }}>
                                  {live.mismatch.breaking ? '\u26a0 BREAKING mismatch' : '\u26a0 mismatch'}:{' '}
                                  {live.mismatch.fields.map(f =>
                                    `${f.field} declared=${f.declared} measured=${f.measured}${f.driftPct ? ` (${f.driftPct}%)` : ''}`
                                  ).join(', ')}
                                  {' '}&mdash; nothing was resynced; fix above and Prepare again.
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {ingestResult && (
                        <div style={{ marginTop: 6, fontSize: 12, fontFamily: 'monospace' }}>
                          {ingestResult.map((r, i) => (
                            <div key={i} style={{ color: r.ok ? '#4caf50' : '#f44336' }}>
                              {r.ok ? '\u2713' : '\u2717'} {r.name} {r.error ? `\u2014 ${r.error}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* Manifest collapsible panel (advanced mode) */}
            {advancedMode && (
              <div style={{ marginBottom: 10, border: '1px solid #555', borderRadius: 8, overflow: 'hidden' }}>
                <div
                  onClick={() => {
                    const opening = !mpdOpen;
                    setMpdOpen(opening);
                    if (opening && masterInst) {
                      fetchManifestForEvent?.(evt, masterInst);
                    }
                  }}
                  style={{
                    padding: '8px 12px', background: 'rgba(128,128,128,0.15)', cursor: 'pointer',
                    fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8, color: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 12 }}>{mpdOpen ? '\u25BC' : '\u25B6'}</span>
                  Manifest
                  <a
                    href={`https://d10tner3hocpa9.cloudfront.net/${evt}/master/manifest.mpd`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ color: '#9a51e2', fontSize: 11, fontWeight: 'normal', marginLeft: 'auto' }}
                  >CloudFront MPD</a>
                </div>
                {mpdOpen && (
                  <div style={{ padding: 12 }}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                      {[['main', 'manifest.mpd'], ['8k', 'manifest_8K.mpd'], ['full', 'manifest_full.mpd']].map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setMpdTab(key)}
                          style={{
                            padding: '4px 12px', borderRadius: 4,
                            background: mpdTab === key ? '#9a51e2' : 'transparent',
                            color: mpdTab === key ? '#fff' : 'inherit',
                            border: mpdTab === key ? '1px solid #9a51e2' : '1px solid #888',
                            cursor: 'pointer', fontWeight: mpdTab === key ? 'bold' : 'normal', fontSize: 12,
                          }}
                        >{label}</button>
                      ))}
                    </div>

                    {manifestMeta[evt]?.source && (
                      <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>Source: {manifestMeta[evt].source}</div>
                    )}

                    {/* Editor textarea */}
                    <textarea
                      value={
                        mpdTab === 'main' ? (mpdEdit[evt] ?? manifestText[evt] ?? '')
                        : mpdTab === '8k' ? (mpdEdit8K[evt] ?? manifestText8K[evt] ?? '')
                        : (mpdEditFull[evt] ?? manifestTextFull[evt] ?? '')
                      }
                      onChange={e => {
                        const val = e.target.value;
                        if (mpdTab === 'main') setMpdEdit(v => ({ ...v, [evt]: val }));
                        else if (mpdTab === '8k') setMpdEdit8K(v => ({ ...v, [evt]: val }));
                        else setMpdEditFull(v => ({ ...v, [evt]: val }));
                      }}
                      style={{
                        width: '100%', minHeight: 250, fontFamily: 'monospace', fontSize: 11,
                        borderRadius: 4, padding: 8, boxSizing: 'border-box',
                        resize: 'vertical', color: '#ccc', background: '#181828',
                        border: '1px solid #444',
                      }}
                      spellCheck={false}
                    />

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        disabled={mpdSaving}
                        onClick={async () => {
                          try {
                            setMpdSaving(true);
                            const main = mpdEdit[evt] ?? manifestText[evt] ?? '';
                            const m8k = mpdEdit8K[evt] ?? manifestText8K[evt] ?? '';
                            const mFull = mpdEditFull[evt] ?? manifestTextFull[evt] ?? '';
                            if (typeof saveAndPushMpd === 'function') {
                              await saveAndPushMpd(evt, main, masterInst, m8k || undefined, mFull || undefined);
                            }
                            setManifestText(v => ({ ...v, [evt]: main }));
                            if (m8k) setManifestText8K(v => ({ ...v, [evt]: m8k }));
                            if (mFull) setManifestTextFull(v => ({ ...v, [evt]: mFull }));
                            // Clear edit buffers
                            setMpdEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEdit8K(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEditFull(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMessage?.('Manifests saved & pushed to master.');
                          } catch (err) {
                            setMessage?.(`Save manifest error: ${err.message}`);
                          } finally {
                            setMpdSaving(false);
                          }
                        }}
                        style={{ padding: '6px 14px', background: mpdSaving ? '#555' : '#4caf50', color: '#fff', border: 'none', borderRadius: 4, cursor: mpdSaving ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >{mpdSaving ? 'Saving…' : 'Save All Variants'}</button>
                      <button
                        onClick={async () => {
                          if (masterInst) {
                            await fetchManifestForEvent?.(evt, masterInst);
                            // Clear edit buffers to show fresh data
                            setMpdEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEdit8K(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEditFull(v => { const n = { ...v }; delete n[evt]; return n; });
                          }
                        }}
                        style={{ padding: '6px 14px', background: '#555', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                      >Reload</button>
                      <button
                        disabled={!masterInst || mpdSaving}
                        onClick={async () => {
                          try {
                            setMessage?.('Rebuilding MPD…');
                            const videoCams = camsOnly.filter(c => detectRoleFromInst(c) === 'video' && ffmpegStat[c.instanceId]);
                            const camMpdData = [];
                            for (const cam of videoCams) {
                              try {
                                const url = `https://d10tner3hocpa9.cloudfront.net/${evt}/${cam.camName}/cam.mpd`;
                                const res = await fetch(url, { cache: 'no-store' });
                                if (res.ok) {
                                  const xml = await res.text();
                                  if (xml && xml.includes('<MPD')) camMpdData.push({ cam, xml });
                                }
                              } catch {}
                            }
                            if (typeof onGenerateAndUploadMpd === 'function') {
                              await onGenerateAndUploadMpd(evt, list, masterInst, camMpdData, { resetAst: false });
                              setMessage?.(`MPD rebuilt & uploaded (${camMpdData.length} cam${camMpdData.length !== 1 ? 's' : ''}).`);
                            }
                            if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
                            // Clear edit buffers
                            setMpdEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEdit8K(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEditFull(v => { const n = { ...v }; delete n[evt]; return n; });
                          } catch (err) {
                            setMessage?.(`Rebuild MPD error: ${err.message}`);
                          }
                        }}
                        style={{ padding: '6px 14px', background: !masterInst ? '#555' : '#9333ea', color: '#fff', border: 'none', borderRadius: 4, cursor: !masterInst ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >Rebuild MPD</button>
                      <button
                        disabled={!masterInst}
                        onClick={async () => {
                          try {
                            setMessage?.('Rebuilding MPD with PTO=0…');
                            const videoCams = camsOnly.filter(c => detectRoleFromInst(c) === 'video' && ffmpegStat[c.instanceId]);
                            const camMpdData = [];
                            for (const cam of videoCams) {
                              try {
                                const url = `https://d10tner3hocpa9.cloudfront.net/${evt}/${cam.camName}/cam.mpd`;
                                const res = await fetch(url, { cache: 'no-store' });
                                if (res.ok) {
                                  const xml = await res.text();
                                  if (xml && xml.includes('<MPD')) camMpdData.push({ cam, xml });
                                }
                              } catch {}
                            }
                            if (typeof onGenerateAndUploadMpd === 'function') {
                              await onGenerateAndUploadMpd(evt, list, masterInst, camMpdData, { resetAst: false, forcePtoZero: true });
                              setMessage?.(`MPD rebuilt with PTO=0 (${camMpdData.length} cam${camMpdData.length !== 1 ? 's' : ''}).`);
                            }
                            if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
                            setMpdEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEdit8K(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEditFull(v => { const n = { ...v }; delete n[evt]; return n; });
                          } catch (err) {
                            setMessage?.(`Rebuild PTO=0 error: ${err.message}`);
                          }
                        }}
                        style={{ padding: '6px 14px', background: !masterInst ? '#555' : '#0891b2', color: '#fff', border: 'none', borderRadius: 4, cursor: !masterInst ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >Rebuild PTO=0</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DASH Settings collapsible panel (advanced mode) */}
            {advancedMode && (
              <div style={{ marginBottom: 10, border: '1px solid #555', borderRadius: 8, overflow: 'hidden' }}>
                <div
                  onClick={() => setDashSettingsOpen(!dashSettingsOpen)}
                  style={{
                    padding: '8px 12px', background: 'rgba(128,128,128,0.15)', cursor: 'pointer',
                    fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8, color: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 12 }}>{dashSettingsOpen ? '\u25BC' : '\u25B6'}</span>
                  DASH Settings
                </div>
                {dashSettingsOpen && (
                  <div style={{ padding: 12 }}>
                    <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
                      JSON settings object to merge with default getDashMediaPlayerLiveSettings() when initialising dash.js for this event.
                    </div>
                    
                    <textarea
                      value={dashSettingsEdit[evt] ?? (dashSettingsContent?.[evt] || JSON.stringify(getDashMediaPlayerLiveSettings(), null, 2))}
                      onChange={e => setDashSettingsEdit(v => ({ ...v, [evt]: e.target.value }))}
                      style={{
                        width: '100%', minHeight: 150, fontFamily: 'monospace', fontSize: 11,
                        borderRadius: 4, padding: 8, boxSizing: 'border-box',
                        resize: 'vertical', color: '#ccc', background: '#181828',
                        border: '1px solid #444',
                      }}
                      spellCheck={false}
                    />
                    
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                      <button
                        disabled={dashSettingsSaving}
                        onClick={async () => {
                          try {
                            setDashSettingsSaving(true);
                            const val = dashSettingsEdit[evt] ?? dashSettingsContent?.[evt] ?? '';
                            if (val.trim() !== '') {
                              // Verify it's valid JSON
                              JSON.parse(val);
                            }
                            if (typeof saveDashSettings === 'function') {
                              await saveDashSettings(evt, val);
                            }
                            if (typeof setDashSettingsContent === 'function') {
                              setDashSettingsContent(v => ({ ...v, [evt]: val }));
                            }
                            setDashSettingsEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMessage?.('DASH Settings saved.');
                          } catch (err) {
                            setMessage?.(`Save DASH Settings error: ${err.message}`);
                          } finally {
                            setDashSettingsSaving(false);
                          }
                        }}
                        style={{ padding: '6px 14px', background: dashSettingsSaving ? '#555' : '#4caf50', color: '#fff', border: 'none', borderRadius: 4, cursor: dashSettingsSaving ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >{dashSettingsSaving ? 'Saving…' : 'Save Settings'}</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Cams table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', fontSize: 12 }}>
                <thead>
                  <tr>
                    {!eventStarted && (
                      <th style={{ borderBottom: '1px solid #eee', padding: '4px', textAlign: 'center', width: 28 }}>
                        <input
                          type="checkbox"
                          title="Select/deselect all"
                          checked={camsOnly.length > 0 && camsOnly.every(c => startSelection[c.instanceId])}
                          onChange={e => {
                            const val = e.target.checked;
                            setStartSelection(prev => {
                              const next = { ...prev };
                              camsOnly.forEach(c => { next[c.instanceId] = val; });
                              return next;
                            });
                          }}
                        />
                      </th>
                    )}
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Cam</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Ingest URL</th>
                    {/* TCP check on port 1935: the SRS server is up and listening.
                        Says nothing about the camera — that's the Ingest column. */}
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}
                        title="SRS server reachable on port 1935 (instance up). Camera publishing is the Ingest column.">SRS</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Ingest</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Encoder</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Speed</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Source</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Monitor</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>MPD</th>
                  </tr>
                </thead>
                <tbody>
                  {camsOnly.map((inst) => {
                    const encoderStatus = getEncoderStatus(inst.instanceId);
                    // Detect if camera is present in the manifest
                    const camsInMpdSet = parseCamsInMpd(evt, manifestText[evt] || '');
                    const inMpd = camsInMpdSet.has((inst.camName || '').toLowerCase());
                    const isExpanded = advancedMode && expandedRowId === inst.instanceId;
                    const totalCols = eventStarted ? 9 : 10;
                    return (
                      <React.Fragment key={inst.instanceId}>
                      <tr
                        style={{ cursor: 'pointer', fontSize: '1em', background: isExpanded ? 'rgba(154,81,226,0.08)' : undefined }}
                        onClick={e => {
                          if (e.target && e.target.dataset && e.target.dataset.mpdbtn) return;
                          if (advancedMode) {
                            const opening = expandedRowId !== inst.instanceId;
                            setExpandedRowId(opening ? inst.instanceId : null);
                            if (opening) {
                              setExpandedTab('preset');
                              setEditCmd(cmdFromDb[inst.instanceId] || '');
                              setLogText('');
                              // detect preset by normalized command match
                              const role = detectRoleFromInst(inst);
                              const normCmd = (cmdFromDb[inst.instanceId] || '').replace(/\s+/g, ' ').trim();
                              const matched = normCmd
                                ? presets.find(p => (!p.role || p.role === role) && (p.command || '').replace(/\s+/g, ' ').trim() === normCmd)
                                : null;
                              setAdvPresetId(matched?._id || '');
                              setAdvPresetName(matched?.name || '');
                              setAdvPresetDesc(matched?.description || '');
                            }
                          } else {
                            setSelectedCamInstanceId(inst.instanceId);
                            setShowPresetPanel(true);
                          }
                        }}
                      >
                        {!eventStarted && (
                          <td
                            style={{
                              borderBottom: '1px solid #f5f5f5',
                              padding: '4px',
                              textAlign: 'center',
                              width: 28,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!startSelection[inst.instanceId]}
                              data-mpdbtn
                              onClick={e => e.stopPropagation()}
                              onChange={e => {
                                manualSelectionRef.current[inst.instanceId] = true;
                                setStartSelection(prev => ({
                                  ...prev,
                                  [inst.instanceId]: e.target.checked,
                                }));
                              }}
                            />
                          </td>
                        )}
                        <td
                          style={{
                            borderBottom: '1px solid #f5f5f5',
                            padding: '4px 6px',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <div>{inst.camName}</div>
                          <input
                            type="text"
                            placeholder="Label..."
                            value={camLabels[inst.instanceId] || ''}
                            onChange={e => updateCamLabel(evt, inst.instanceId, e.target.value)}
                            onClick={e => e.stopPropagation()}
                            style={{
                              width: '100%',
                              border: 'none',
                              background: 'transparent',
                              color: '#ccc',
                              fontSize: 10,
                              textAlign: 'center',
                              outline: 'none',
                              marginTop: 2,
                              padding: '1px 0',
                            }}
                          />
                          <div style={{ fontSize: 10, color: '#999' }}>{inst.instanceType || 'g6.2xlarge'}</div>
                        </td>
                        <td
                          style={{
                            borderBottom: '1px solid #f5f5f5',
                            padding: '4px 6px',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <div>{`rtmp://${inst.publicIp}/live/8k`}</div>
                          <div style={{ marginTop: 2 }}>{`srt://${inst.publicIp}:10080`}</div>
                          {/* Elastic IP picker: the rig publishes to a FIXED ip,
                              so the EIP follows the fresh instance of each event.
                              AllowReassociation on the backend = the usual
                              console "associate + force reassociation". */}
                          {advancedMode && Array.isArray(eips) && (
                            <div style={{ marginTop: 4, display: 'flex', gap: 4, alignItems: 'center' }}
                                 onClick={e => e.stopPropagation()}>
                              <select
                                value={eipSel[inst.instanceId] || ''}
                                onChange={e => setEipSel(prev => ({ ...prev, [inst.instanceId]: e.target.value }))}
                                style={{ fontSize: 11, padding: 2, background: '#222', color: '#fff', border: '1px solid #555', borderRadius: 4, maxWidth: 170 }}
                              >
                                <option value="">— Elastic IP —</option>
                                {/* EIPs are region-scoped: only offer the pool of the
                                    instance's region (cross-region associate fails).
                                    No region info (older backend) → show everything. */}
                                {eips
                                  .filter(a => !a.region || !inst.region || a.region === inst.region)
                                  .map(a => (
                                  <option key={a.allocationId} value={a.allocationId}>
                                    {a.name ? `${a.name} — ${a.publicIp}` : a.publicIp}
                                    {a.instanceId === inst.instanceId ? ' (this cam)'
                                      : a.instanceId ? ` (in use: ${a.instanceName})` : ' (free)'}
                                  </option>
                                ))}
                              </select>
                              <button
                                disabled={eipBusy || !eipSel[inst.instanceId]}
                                onClick={() => associateEip(inst)}
                                style={{
                                  fontSize: 11, padding: '2px 8px', border: 'none', borderRadius: 4,
                                  background: eipBusy || !eipSel[inst.instanceId] ? '#9e9e9e' : '#9a51e2',
                                  color: '#fff', cursor: eipBusy || !eipSel[inst.instanceId] ? 'not-allowed' : 'pointer',
                                }}
                              >
                                Assign
                              </button>
                            </div>
                          )}
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          <Badge
                            ok={!!rtmpOk[inst.instanceId]}
                            on={COLORS.ok}
                            off={COLORS.err}
                            labelOk="OK"
                            labelOff="No"
                          />
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          {(() => {
                            const so = sourceOverrideMap[inst.instanceId];
                            const badge = (() => {
                              if (!so) return <span style={styles.badge(COLORS.idle)}>-</span>;
                              const camActive = so.activeSource && so.activeSource !== 'slate';
                              if (so.forcedSource === 'slate') {
                                // Forced to slate — camera status from RTMP check
                                return <Badge ok={!!rtmpOk[inst.instanceId]} on={COLORS.ok} off={COLORS.err} labelOk="OK" labelOff="No" />;
                              }
                              return <Badge ok={!!camActive} on={COLORS.ok} off={COLORS.err} labelOk="OK" labelOff="No" />;
                            })();
                            const kb = ingestKbps?.[inst.instanceId];
                            // kbps hangs below via absolute positioning so the row
                            // keeps its height and the badge stays aligned with its
                            // neighbor columns.
                            return (
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                {badge}
                                {kb != null && kb > 0 && (
                                  <span style={{
                                    position: 'absolute', top: 'calc(100% + 3px)', left: '50%',
                                    transform: 'translateX(-50%)', fontSize: 11,
                                    color: '#888', lineHeight: 1, whiteSpace: 'nowrap',
                                  }}>
                                    {(kb / 1000).toFixed(1)} Mb
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <span style={styles.badge(encoderStatus.color)}>{encoderStatus.label}</span>
                            {eventStarted && encoderStatus.label === 'Stopped' && (
                              <button
                                style={miniIconBtn(COLORS.ok)}
                                data-mpdbtn
                                title={`Start encoding on ${inst.camName}`}
                                onClick={e => { e.stopPropagation(); startSingleCamEncoding(evt, inst); }}
                              >
                                ▶
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          {encoderStatus.label === 'Stopped'
                            ? <span style={styles.badge(COLORS.idle)}>-</span>
                            : renderSpeedPill(speedMap[inst.instanceId])}
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          {(() => {
                            const so = sourceOverrideMap[inst.instanceId];
                            if (!so) return <span style={styles.badge(COLORS.idle)}>-</span>;
                            const forced = so.forcedSource === 'slate';
                            const isSlate = so.activeSource === 'slate' || so.activeSource === null;
                            const label = forced ? 'LOGO' : isSlate ? 'SLATE' : 'CAM';
                            const color = forced ? '#2196F3' : isSlate ? '#ff9800' : COLORS.ok;
                            const title = forced
                              ? 'Manual: logo forzado — click para volver a auto'
                              : isSlate
                                ? 'Auto: sin señal (slate) — click para forzar logo'
                                : 'Auto: en vivo — click para forzar logo';
                            return (
                              <button
                                style={{
                                  ...styles.badge(color),
                                  border: forced ? '2px solid #fff' : 'none',
                                  cursor: 'pointer',
                                  fontSize: 11,
                                  minWidth: 44,
                                }}
                                data-mpdbtn
                                title={title}
                                onClick={async e => {
                                  e.stopPropagation();
                                  try {
                                    await api.setSourceOverride(inst.instanceId, forced ? 'auto' : 'slate');
                                  } catch (err) {
                                    setMessage?.(`Source override error: ${err.message}`);
                                  }
                                }}
                              >
                                {label}
                              </button>
                            );
                          })()}
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          <button
                            style={{
                              ...styles.badge('#2196F3'),
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: 14,
                            }}
                            data-mpdbtn
                            title="Open camera monitor in popup"
                            onClick={e => {
                              e.stopPropagation();
                              const CLOUDFRONT_BASE = 'https://d10tner3hocpa9.cloudfront.net';
                              const camPath = `${evt}/${inst.camName}`;
                              const cameraData = { name: inst.camName, path: camPath };
                              const width = 600;
                              const height = 800;
                              const left = (window.screen.width - width) / 2;
                              const top = (window.screen.height - height) / 2;
                              const popupUrl = `/camera-monitor-popup?camera=${encodeURIComponent(JSON.stringify(cameraData))}&baseUrl=${encodeURIComponent(CLOUDFRONT_BASE)}&instanceId=${encodeURIComponent(inst.instanceId)}`;
                              window.open(popupUrl, `camera-monitor-${inst.camName}`, `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
                            }}
                          >
                            📊
                          </button>
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          {(() => {
                            const role = detectRoleFromInst(inst);
                            const camName = inst.camName || '';
                            const vState = getFragEnabled(evt, camName, 'video');
                            const aState = getFragEnabled(evt, camName, 'audio');
                            const hasAnyFrag = vState !== undefined || aState !== undefined;

                            if (!inMpd && !hasAnyFrag) {
                              return (
                                <button
                                  style={miniIconBtn(COLORS.ok)}
                                  data-mpdbtn
                                  title="Add this cam to the event"
                                  onClick={e => { e.stopPropagation(); handleAddCam(evt, inst, masterInst, manifestText[evt] || ''); }}
                                >
                                  +
                                </button>
                              );
                            }

                            /* Studio mode: simple +/− only */
                            if (!advancedMode) {
                              return (
                                <div style={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
                                  <span style={{ color: COLORS.ok, fontSize: 13, fontWeight: 'bold' }} title="In manifest">✓</span>
                                  <button
                                    style={{ ...miniIconBtn(COLORS.err), width: 24, height: 24, fontSize: 14, borderRadius: 4 }}
                                    data-mpdbtn
                                    title="Remove this cam from the event"
                                    onClick={e => { e.stopPropagation(); handleRemoveCam(evt, inst, masterInst, manifestText[evt] || ''); }}
                                  >
                                    −
                                  </button>
                                </div>
                              );
                            }

                            /* Advanced mode: V/A toggles + remove */
                            const toggleBtnStyle = (on, color) => ({
                              ...styles.badge(color || (on ? COLORS.ok : COLORS.err)),
                              cursor: 'pointer',
                              border: 'none',
                              fontSize: 11,
                              minWidth: 44,
                              opacity: on ? 1 : 0.6,
                            });

                            return (
                              <div style={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
                                {role === 'video' && vState !== undefined && (
                                  <button
                                    style={toggleBtnStyle(vState)}
                                    data-mpdbtn
                                    title={`${vState ? 'Disable' : 'Enable'} video for ${camName}`}
                                    onClick={e => { e.stopPropagation(); handleToggleFragment(evt, camName, 'video', !vState, masterInst); }}
                                  >
                                    video
                                  </button>
                                )}
                                {aState !== undefined && (
                                  <button
                                    style={toggleBtnStyle(aState)}
                                    data-mpdbtn
                                    title={`${aState ? 'Disable' : 'Enable'} audio for ${camName}`}
                                    onClick={e => { e.stopPropagation(); handleToggleFragment(evt, camName, 'audio', !aState, masterInst); }}
                                  >
                                    audio
                                  </button>
                                )}
                                <button
                                  style={toggleBtnStyle(false, COLORS.err)}
                                  data-mpdbtn
                                  title="Remove this cam from the event"
                                  onClick={e => { e.stopPropagation(); handleRemoveCam(evt, inst, masterInst, manifestText[evt] || ''); }}
                                >
                                  −
                                </button>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                      {/* Advanced mode: expandable detail panel */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={totalCols} style={{ padding: 0, borderBottom: '2px solid #9a51e2' }}>
                            <div style={{ background: '#1a1a2e', padding: '12px 16px' }}>
                              {/* Tabs */}
                              <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderBottom: '1px solid #444' }}>
                                {['preset', 'delay', 'logs'].map(tab => (
                                  <button
                                    key={tab}
                                    onClick={e => { e.stopPropagation(); setExpandedTab(tab); }}
                                    style={{
                                      padding: '8px 20px',
                                      background: expandedTab === tab ? '#9a51e2' : 'transparent',
                                      color: '#fff',
                                      border: 'none',
                                      borderBottom: expandedTab === tab ? '2px solid #fff' : '2px solid transparent',
                                      cursor: 'pointer',
                                      fontWeight: expandedTab === tab ? 600 : 400,
                                      fontSize: 13,
                                      textTransform: 'capitalize',
                                    }}
                                  >
                                    {tab}
                                  </button>
                                ))}
                              </div>

                              {/* Tab: Preset */}
                              {expandedTab === 'preset' && (() => {
                                const role = detectRoleFromInst(inst);
                                const filtered = presets.filter(p => !p.role || p.role === role);
                                const selPreset = filtered.find(p => p._id === advPresetId) || null;
                                return (
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <label style={{ color: '#9a51e2', fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>Preset:</label>
                                      <select
                                        value={advPresetId}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => {
                                          const id = e.target.value;
                                          setAdvPresetId(id);
                                          const p = filtered.find(x => x._id === id);
                                          if (p) setEditCmd(p.command || '');
                                        }}
                                        style={{ flex: 1, padding: 6, borderRadius: 6, background: '#181828', color: '#fff', border: '1px solid #555', fontSize: 12 }}
                                      >
                                        <option value="">-- Select --</option>
                                        {filtered.map(p => (
                                          <option key={p._id} value={p._id}>{p.name}{p.isDefault ? ' (default)' : ''}</option>
                                        ))}
                                      </select>
                                      <button
                                        onClick={async e => {
                                          e.stopPropagation();
                                          if (!selPreset) return;
                                          try {
                                            await api.saveEventCmd(inst.eventName || evt, { instanceId: inst.instanceId, command: (selPreset.command || '').trim(), role });
                                            await api.updateNodeScript(inst.instanceId, selPreset.command || '');
                                            setEditCmd(selPreset.command || '');
                                            setMessage?.(`Preset "${selPreset.name}" applied to ${inst.camName}`);
                                          } catch (err) { setMessage?.(`Apply error: ${err.message}`); }
                                        }}
                                        disabled={!selPreset}
                                        style={{ padding: '5px 14px', background: selPreset ? '#4caf50' : '#555', color: '#fff', border: 'none', borderRadius: 6, cursor: selPreset ? 'pointer' : 'default', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}
                                      >Apply</button>
                                    </div>
                                    {selPreset ? (
                                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#181828', borderRadius: 6, border: '1px solid #444', fontSize: 11 }}>
                                        <div style={{ color: '#9a51e2', fontWeight: 600 }}>{selPreset.name}{selPreset.isDefault ? ' (default)' : ''}</div>
                                        {selPreset.description && <div style={{ color: '#aaa', marginTop: 4 }}>{selPreset.description}</div>}
                                      </div>
                                    ) : cmdFromDb[inst.instanceId] ? (
                                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#181828', borderRadius: 6, border: '1px solid #555', fontSize: 11 }}>
                                        <div style={{ color: '#ff9800', fontWeight: 600 }}>Custom command (no preset match)</div>
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })()}

                              {/* Tab: Command */}
                              {/* Tab: Logs */}
                              {/* Tab: Delay */}
                              {expandedTab === 'delay' && (
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <label style={{ color: '#9a51e2', fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>Content Offset (seconds):</label>
                                    <input
                                      type="number"
                                      step="0.5"
                                      placeholder="0"
                                      value={contentOffsets[(inst.camName || '').toLowerCase()] ?? ''}
                                      onChange={e => {
                                        const val = e.target.value;
                                        updateContentOffset(evt, inst.camName, val === '' ? 0 : parseFloat(val) || 0);
                                      }}
                                      onClick={e => e.stopPropagation()}
                                      style={{
                                        width: 80,
                                        padding: '6px 8px',
                                        borderRadius: 6,
                                        background: '#181828',
                                        color: '#fff',
                                        border: '1px solid #555',
                                        fontSize: 13,
                                        textAlign: 'center',
                                      }}
                                    />
                                    <span style={{ color: '#888', fontSize: 11 }}>positive = delay this camera &nbsp;|&nbsp; negative = advance</span>
                                  </div>
                                  <p style={{ color: '#666', fontSize: 11, margin: '8px 0 0' }}>
                                    Added to the auto-calculated PTO on next MPD rebuild. Click "Rebuild MPD" to apply.
                                    <br />SRT ingest has ~3s less latency than RTMP. If mixing protocols in the same event, set +3.0s (or more) on SRT cameras to align with RTMP.
                                  </p>
                                </div>
                              )}

                              {expandedTab === 'logs' && (() => {
                                const fetchLog = async () => {
                                  try {
                                    const res = await authFetch(`${API_BASE}/event-wizard/proxy/${inst.instanceId}/ffmpeg/log`);
                                    const text = await res.text();
                                    setLogText(text);
                                  } catch { setLogText('Log fetch error'); }
                                };
                                if (!logText) fetchLog();
                                return (
                                  <div>
                                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                                      <button
                                        onClick={e => { e.stopPropagation(); fetchLog(); }}
                                        style={{ padding: '5px 14px', background: '#607d8b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                                      >Refresh Log</button>
                                    </div>
                                    <pre
                                      onClick={e => e.stopPropagation()}
                                      style={{ background: '#181828', border: '1px solid #555', borderRadius: 6, padding: 10, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11, color: '#e0e0e0', maxHeight: 300, overflowY: 'auto', margin: 0 }}
                                    >{logText || 'Loading...'}</pre>
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                  {!camsOnly.length &&
                    pendingForEvt.filter((p) => (p.role || '').toLowerCase() !== 'master').length ===
                      0 && (
                      <tr>
                        <td colSpan={eventStarted ? 9 : 10} style={{ padding: '4px 6px', color: '#999' }}>
                          No cameras
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>

            {/* MPD URL (studio mode — simple display only) */}
            <div style={{ margin: '12px 0' }}>
              <p>
                <strong>Event MPD:</strong>{' '}
                <a href={`https://d10tner3hocpa9.cloudfront.net/${evt}/master/manifest.mpd`} target="_blank" rel="noopener noreferrer" style={{ color: '#9a51e2' }}>
                  {`https://d10tner3hocpa9.cloudfront.net/${evt}/master/manifest.mpd`}
                </a>
              </p>
            </div>

            {/* Camera drift / sync panel */}
            <div style={{ margin: '12px 0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => (openDriftEvt === evt ? setOpenDriftEvt(null) : fetchDrift(evt, list))}
                disabled={driftLoadingEvt === evt}
                style={driftBtn(openDriftEvt === evt ? '#9e9e9e' : COLORS.neutral, driftLoadingEvt !== evt)}
                title="Compare drift across all cameras of this event"
              >
                {driftLoadingEvt === evt ? 'Loading…' : openDriftEvt === evt ? 'Hide sync' : '📊 Camera sync'}
              </button>
              <button
                onClick={() => (openTrackOrderEvt === evt ? setOpenTrackOrderEvt(null) : fetchTrackOrder(evt))}
                disabled={trackOrderBusyEvt === evt}
                style={driftBtn(openTrackOrderEvt === evt ? '#9e9e9e' : COLORS.neutral, trackOrderBusyEvt !== evt)}
                title="Reorder cameras and audios in the manifest — the first one is the viewer default"
              >
                {trackOrderBusyEvt === evt ? 'Loading…' : openTrackOrderEvt === evt ? 'Hide order' : '🔀 Track order'}
              </button>
            </div>

            {/* Track order panel (IR-196) */}
            {openTrackOrderEvt === evt && trackOrderByEvent[evt] && (() => {
              const cur = trackOrderByEvent[evt];
              const busy = trackOrderBusyEvt === evt;
              const listBox = (kind, title, items) => (
                <div style={{ minWidth: 260 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
                  {!items.length && <div style={{ color: '#999', fontSize: 12 }}>None in manifest</div>}
                  {items.map((item, i) => (
                    <div key={item.camName} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                      <span style={{ width: 18, textAlign: 'right', color: i === 0 ? COLORS.ok : '#888', fontSize: 12 }}>{i + 1}.</span>
                      <span style={{ flex: 1, fontSize: 13 }}>
                        <span style={{ fontFamily: 'monospace' }}>{item.camName}</span>
                        {item.label && <span style={{ color: '#bbb' }}> — {item.label}</span>}
                      </span>
                      <button
                        disabled={busy || i === 0}
                        onClick={() => moveTrack(evt, kind, i, -1)}
                        style={{ ...miniIconBtn('#607d8b'), width: 24, height: 24, fontSize: 13, opacity: i === 0 ? 0.3 : 1 }}
                        title="Move up"
                      >↑</button>
                      <button
                        disabled={busy || i === items.length - 1}
                        onClick={() => moveTrack(evt, kind, i, +1)}
                        style={{ ...miniIconBtn('#607d8b'), width: 24, height: 24, fontSize: 13, opacity: i === items.length - 1 ? 0.3 : 1 }}
                        title="Move down"
                      >↓</button>
                    </div>
                  ))}
                </div>
              );
              return (
                <div style={{ margin: '12px 0', borderTop: '1px dashed #555', paddingTop: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                    <strong>Track order</strong>
                    <button
                      onClick={() => setOpenTrackOrderEvt(null)}
                      style={{ marginLeft: 'auto', background: '#9e9e9e', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}
                    >Close</button>
                  </div>
                  <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                    {listBox('videos', 'Cameras', cur.videos)}
                    {listBox('audios', 'Audios', cur.audios)}
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      disabled={busy || !cur.dirty}
                      onClick={() => applyTrackOrder(evt)}
                      style={driftBtn(COLORS.warn, !busy && cur.dirty)}
                    >{busy ? 'Applying…' : 'Apply & rebuild manifest'}</button>
                    {cur.dirty && !busy && <span style={{ fontSize: 12, color: COLORS.warn }}>Unsaved changes</span>}
                  </div>
                </div>
              );
            })()}

            {openDriftEvt === evt && driftByEvent[evt] && (() => {
              const { rows = [], loadedAt } = driftByEvent[evt];
              const { ref, spread, byCam } = driftAnalysis(rows);
              const th = { borderBottom: '1px solid #eee', padding: '8px', textAlign: 'left' };
              const td = { borderBottom: '1px solid #f5f5f5', padding: '8px' };
              const fmt = (v, unit = 's') => (v == null ? <span style={{ color: '#999' }}>—</span> : `${v.toFixed(2)}${unit}`);

              return (
                <div style={{ marginTop: 12, borderTop: '1px dashed #ddd', paddingTop: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                    <strong>Camera sync</strong>
                    {spread != null && (
                      <span style={styles.badge(spread > DRIFT_SPREAD_WARN_S ? COLORS.warn : COLORS.ok)}>
                        spread {spread.toFixed(2)}s
                      </span>
                    )}
                    {ref && <span style={{ fontSize: 12, color: '#aaa' }}>reference: <b>{ref.camName}</b> (most drift — furthest behind)</span>}
                    <span style={{ fontSize: 12, color: '#666' }}>
                      updated {new Date(loadedAt).toLocaleTimeString()} · every {DRIFT_POLL_MS / 1000}s
                    </span>
                    <button
                      onClick={() => setOpenDriftEvt(null)}
                      style={{ marginLeft: 'auto', background: '#9e9e9e', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}
                    >Close</button>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={th}>Cam</th>
                          <th style={th}>Lag</th>
                          <th style={th}>Drift</th>
                          <th style={th}>Off by</th>
                          <th style={th}>Source</th>
                          <th style={th}>Switches</th>
                          <th style={th}>Last switch</th>
                          <th style={th}>Offset now</th>
                          <th style={th}>Suggested</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(r => {
                          const key = (r.camName || '').toLowerCase();
                          const delta = byCam[key];
                          const current = Number(contentOffsets[key] ?? 0) || 0;
                          return (
                            <tr key={r.instanceId} style={{ fontSize: '0.95em' }}>
                              <td style={{ ...td, fontWeight: 600 }}>{r.camName}</td>
                              {r.ok ? (
                                <>
                                  <td style={{ ...td, color: r.lagSeconds != null && r.lagSeconds > 2 ? COLORS.warn : '#ddd' }}>{fmt(r.lagSeconds)}</td>
                                  <td style={{ ...td, fontFamily: 'monospace' }}>{fmt(r.driftSeconds)}</td>
                                  {/* Unknown must not read as fine: only a measured zero is green. */}
                                  <td style={{ ...td, fontFamily: 'monospace', color: delta == null ? '#999' : delta === 0 ? COLORS.ok : COLORS.warn }}>
                                    {delta == null ? 'no data' : delta === 0 ? 'aligned' : `+${delta.toFixed(1)}s`}
                                  </td>
                                  <td style={{ ...td, color: r.activeSource === 'slate' ? COLORS.warn : COLORS.ok }}>{r.activeSource ?? '—'}</td>
                                  <td style={td}>{r.switchCount ?? '—'}</td>
                                  <td style={td}>{r.lastSwitch ? new Date(r.lastSwitch).toLocaleTimeString() : '—'}</td>
                                  <td style={{ ...td, fontFamily: 'monospace' }}>{current ? `${current}s` : '—'}</td>
                                  <td style={{ ...td, fontFamily: 'monospace', color: delta != null && delta !== current ? COLORS.warn : '#888' }}>
                                    {delta == null ? '—' : `${delta}s`}
                                  </td>
                                </>
                              ) : (
                                <td colSpan={8} style={{ ...td, color: COLORS.err }}>unreachable{r.error ? ` — ${r.error}` : ''}</td>
                              )}
                            </tr>
                          );
                        })}
                        {!rows.length && <tr><td colSpan={9} style={{ ...td, color: '#999' }}>No cameras</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                    <button
                      onClick={async () => {
                        const changes = Object.entries(byCam).filter(([k, v]) => (Number(contentOffsets[k] ?? 0) || 0) !== v);
                        if (!changes.length) return setMessage?.('Offsets already match the measured drift.');
                        if (!confirmDanger(
                          `Overwrite the content offset of ${changes.length} camera(s) with the measured misalignment and rebuild the manifest?\n\n` +
                          changes.map(([k, v]) => `  ${k}: ${(Number(contentOffsets[k] ?? 0) || 0)}s → ${v}s`).join('\n') +
                          `\n\nViewers reposition on their next manifest refresh.`
                        )) return;
                        try {
                          setMessage?.('Aligning offsets…');
                          // Persist synchronously, NOT through the 500ms-debounced
                          // updateContentOffset: the rebuild below reads contentOffsets
                          // from the doc, and racing the debounce meant rebuilding with
                          // the OLD offsets — the "Align does nothing" bug.
                          for (const [k, v] of changes) {
                            const row = rows.find(r => (r.camName || '').toLowerCase() === k);
                            setContentOffsets(prev => ({ ...prev, [k]: v }));
                            await api.saveContentOffset(evt, row?.camName || k, v);
                          }
                          const masterInst = Object.values(grouped).flat()
                            .find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
                          await api.rebuildMpd(evt, [], masterInst?.instanceId);
                          setMessage?.(`Offsets aligned on ${changes.length} camera(s) and manifest rebuilt.`);
                          await fetchDrift(evt, list, { silent: true });
                        } catch (e) {
                          setMessage?.(`Align failed: ${e.response?.data?.message || e.message}`);
                        }
                      }}
                      disabled={!ref}
                      style={driftBtn(COLORS.warn, !!ref)}
                    >Align offsets to drift</button>
                    {(() => {
                      // A single AST serves every camera, so the shift is bounded by the
                      // SMALLEST drift — go past it and that camera is positioned beyond
                      // segments it has not produced, and it stalls. The margin leaves a
                      // segment of slack rather than landing exactly on the edge.
                      const usable = rows.filter(r => r.ok && r.driftSeconds != null);
                      const minDrift = usable.length ? Math.min(...usable.map(r => r.driftSeconds)) : 0;
                      const applied = driftByEvent[evt]?.astShiftSeconds || 0;
                      // Headroom is the measured drift MINUS whatever has already been
                      // applied. Since /drift never moves in response to a re-anchor,
                      // this subtraction is the only thing that makes the button
                      // converge instead of offering the same shift forever.
                      const shift = Math.floor(minDrift - applied - 1);
                      const can = usable.length > 0 && shift >= 1;
                      const masterInst = Object.values(grouped).flat()
                        .find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
                      return (
                        <>
                          <button
                            disabled={!can}
                            title={can
                              ? `Move the AST back ${shift}s — ${applied}s of the ${minDrift.toFixed(1)}s measured drift already applied`
                              : applied > 0
                                ? `No headroom left: ${applied}s already applied of ${minDrift.toFixed(1)}s measured drift`
                                : 'Needs at least 2s of drift on every camera — nothing safe to recover'}
                            onClick={async () => {
                              if (!confirmDanger(
                                `Re-anchor "${evt}": move the AST back ${shift}s?\n\n` +
                                `Smallest drift ${minDrift.toFixed(1)}s, already applied ${applied}s ` +
                                `→ ${shift}s available with ~1s of slack.\n\n` +
                                `Every connected viewer repositions. The original AST is kept, so this can be undone.`
                              )) return;
                              try {
                                await api.reanchorAst(evt, { shiftSeconds: shift, measuredMinDriftSeconds: minDrift });
                                await api.rebuildMpd(evt, [], masterInst?.instanceId);
                                setMessage?.(`AST re-anchored −${shift}s for "${evt}" and manifest rebuilt.`);
                                await fetchDrift(evt, list, { silent: true });
                              } catch (e) {
                                setMessage?.(`Re-anchor failed: ${e.response?.data?.message || e.message}`);
                              }
                            }}
                            style={driftBtn(COLORS.neutral, can)}
                          >Re-anchor AST {can ? `(−${shift}s)` : ''}</button>
                          {applied > 0 && (
                            <button
                              title={`Put the AST back to the true event start (${applied}s of shift applied)`}
                              onClick={async () => {
                                if (!confirmDanger(
                                  `Restore the original AST for "${evt}"?\n\n` +
                                  `Undoes the ${applied}s of re-anchoring — latency goes back up by that much ` +
                                  `and every connected viewer repositions.`
                                )) return;
                                try {
                                  await api.reanchorAst(evt, { restore: true });
                                  await api.rebuildMpd(evt, [], masterInst?.instanceId);
                                  setMessage?.(`Original AST restored for "${evt}" (+${applied}s) and manifest rebuilt.`);
                                  await fetchDrift(evt, list, { silent: true });
                                } catch (e) {
                                  setMessage?.(`Restore failed: ${e.response?.data?.message || e.message}`);
                                }
                              }}
                              style={driftBtn('#9e9e9e')}
                            >Restore original AST (+{applied}s)</button>
                          )}
                        </>
                      );
                    })()}
                    <span style={{ fontSize: 11, color: '#888', maxWidth: 620 }}>
                      Corrects only the misalignment caused by <b>drift</b>. Cameras that started at
                      different times are a separate problem, handled by the auto PTO — so if the picture
                      is still off after this, the fix is a synchronized restart, not a bigger offset.
                      <br />Overwrites the manual content offsets, replacing any value set by hand for
                      inherent signal latency (e.g. SRT vs RTMP). Applies immediately: offsets are saved
                      and the MPD is rebuilt in the same action.
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* NEW: First segment results panel */}
            {openFirstSegEvt === evt && firstSegByEvent[evt] && (
              <div style={{ marginTop: 12, borderTop: '1px dashed #ddd', paddingTop: 12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <strong>First segment creation times</strong>
                  <span style={{ fontSize:12, color:'#666' }}>
                    (loaded {new Date(firstSegByEvent[evt].loadedAt).toLocaleString()})
                  </span>
                  <button
                    onClick={() => setOpenFirstSegEvt(null)}
                    style={{ marginLeft:'auto', background:'#9e9e9e', color:'#fff', border:'none', borderRadius:4, padding:'4px 8px', cursor:'pointer' }}
                  >
                    Close
                  </button>
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ textAlign:'left' }}>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>Cam</th>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>Source</th>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>Created (ISO)</th>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>File</th>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(firstSegByEvent[evt].rows || []).map(row => (
                        <tr key={row.instanceId} style={{ fontSize:'0.95em' }}>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px' }}>{row.camName}</td>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px', textTransform:'capitalize' }}>{row.source}</td>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px', fontFamily:'monospace' }}>
                            {row.createdAtIso || <span style={{ color:'#999' }}>—</span>}
                          </td>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px' }}>
                            <a href={row.url} target="_blank" rel="noopener noreferrer" style={{ color: '#9a51e2', textDecoration: 'underline' }}>{row.url}</a>
                          </td>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px' }}>
                            {row.ok
                              ? <span style={styles.badge(COLORS.ok)}>OK</span>
                              : <span style={styles.badge(COLORS.err)} title={row.error || ''}>Error</span>}
                          </td>
                        </tr>
                      ))}
                      {(!firstSegByEvent[evt].rows || firstSegByEvent[evt].rows.length === 0) && (
                        <tr>
                          <td colSpan={5} style={{ padding:'8px', color:'#999' }}>No data</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        );
      })}
    </div>
  );
}
